import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { createPost, presignCover, uploadToSignedUrl, uploadCoverDirect, finalizePresignedCoverUpload, patchPostBody, preflightPublishReadiness } from '../api/posts'
import { createJobDraft, publishJob } from '../api/jobs'
import { getWalletSummary } from '../api/wallet'
import { useAIEditor } from '../hooks/useAIEditor'
import { generateCoverImage } from '../api/ai'
import { getModerationCheckStatus } from '../api/moderation'
import TitleFields from '../features/editor/TitleFields'
import SaveWordStatus from '../features/editor/SaveWordStatus'
import PublishActions from '../features/editor/PublishActions'
import DraftControls from '../features/editor/DraftControls'
import PreviewPanel from '../features/editor/PreviewPanel'
import InsufficientPixeModal from '../features/editor/InsufficientPixeModal'
import MicroTextComposer from '../features/editor/MicroTextComposer'
import MicroTemplatePicker from '../features/editor/MicroTemplatePicker'
import MicroPreviewCard from '../features/editor/MicroPreviewCard'
import MicroReaderPreview from '../features/editor/MicroReaderPreview'
import { DEFAULT_MICRO_TEMPLATE_ID, MICRO_TEMPLATES } from '../features/editor/microTemplates'
import { useMe } from '../hooks/useMe'
import { usePostCategories } from '../hooks/usePostCategories'
import { createDraft, updateDraft, publishDraft, deleteDraft, getDraft, listDrafts as listServerDrafts } from '../api/drafts'
import { FiBold, FiItalic, FiUnderline, FiType, FiList, FiLink, FiImage, FiX, FiCode, FiMinus, FiRotateCcw, FiRotateCw, FiAlignLeft, FiAlignCenter, FiAlignRight, FiAlignJustify } from 'react-icons/fi'
import { RiListOrdered, RiDoubleQuotesL, RiMarkPenLine, RiStrikethrough2 } from 'react-icons/ri'
import { BiCodeBlock } from 'react-icons/bi'
import { MdFormatClear } from 'react-icons/md'
import RichEditor, { type RichEditorHandle, type EditorToolbarState } from '../components/editor/RichEditor'
import PublishSettingsDrawer from '../components/publish/PublishSettingsDrawer'
import LocalPublishOptions, { type LocalDistanceOption, type LocalTargetMode } from '../components/publish/LocalPublishOptions'
import { buildLocalPayload, normalizeProfileCityLabel, resolveGeoDeniedFallback } from '../components/publish/localTargeting'
import EventDateTimePicker from '../components/publish/EventDateTimePicker'
import type { EditorPublishSettings } from '../types/editor'
import { DEFAULT_EDITOR_PUBLISH_SETTINGS } from '../types/editor'
import { getReadinessBlockingMessage } from './publishPreflight'
import { isEventEndBeforeStart, isEventStartMissing } from './publishValidation'
import { getMyCreatorSubscriptionSettings, listMyCreatorTiers, type CreatorSubscriptionSettings, type CreatorTier } from '../api/creatorSubscriptions'
import { accessLabel, accessRequiresPPV, isAccessOptionDisabled, normalizeAccessSelection, type AccessLevel } from '../features/editor/access'

export default function PublishEditor() {
  type BodyPatchSource = 'publish' | 'save_draft'
  type BodySaveStatus = {
    jsonAvailable: boolean
    mode: 'json' | 'html'
    jsonChars: number
    jsonNodes: number
    source: BodyPatchSource | null
    updatedAt: number | null
  }

  const authed = Boolean(getToken())
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const { me } = useMe(authed)
  const { items: categories } = usePostCategories()
  // header menu removed; shared NavBar handles user menu

  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [format, setFormat] = useState<'micro' | 'zine' | 'book' | 'event' | 'job'>(() => {
    try {
      const raw = (localStorage.getItem('draft:format') || '').trim().toLowerCase()
      if (raw === 'micro' || raw === 'zine' || raw === 'book' || raw === 'event' || raw === 'job') return raw
    } catch {}
    return 'micro'
  })
  const [category, setCategory] = useState<string>(() => {
    try {
      return (localStorage.getItem('draft:category') || '').trim().toLowerCase()
    } catch {
      return ''
    }
  })
  const [microText, setMicroText] = useState<string>(() => {
    try {
      return String(localStorage.getItem('draft:micro_text') || '')
    } catch {
      return ''
    }
  })
  const [microTemplateId, setMicroTemplateId] = useState<string>(() => {
    try {
      const saved = String(localStorage.getItem('draft:micro_template_id') || '').trim()
      if (saved) return saved
      const editorial = MICRO_TEMPLATES.find((t) => t.id === DEFAULT_MICRO_TEMPLATE_ID)?.id
      return editorial || MICRO_TEMPLATES[0].id
    } catch {
      return MICRO_TEMPLATES.find((t) => t.id === DEFAULT_MICRO_TEMPLATE_ID)?.id || MICRO_TEMPLATES[0].id
    }
  })
  const [microAdvancedOpen, setMicroAdvancedOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('draft:micro_advanced_open') === '1'
    } catch {
      return false
    }
  })

  const microCategoryName = useMemo(() => {
    const slug = String(category || '').trim().toLowerCase()
    if (!slug) return ''
    const hit = (categories || []).find((c) => String(c.slug || '').toLowerCase() === slug)
    return hit?.name || slug
  }, [categories, category])
  const profileCityLabel = useMemo(() => {
    const p: any = me?.profile || {}
    const fromLocation = typeof p?.from_location === 'string' ? p.from_location.trim() : ''
    const fromLocationLabel = typeof p?.from_location_label === 'string' ? p.from_location_label.trim() : ''
    return normalizeProfileCityLabel(fromLocation || fromLocationLabel || '')
  }, [me])
  const [body, setBody] = useState('')
  const [coverUrl, setCoverUrl] = useState<string | undefined>(undefined)
  const [coverLandscapeUrl, setCoverLandscapeUrl] = useState<string | undefined>(undefined)
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | undefined>(undefined)
  const [coverLandscapePreviewUrl, setCoverLandscapePreviewUrl] = useState<string | undefined>(undefined)
  const [uploading, setUploading] = useState(false)
  const [editorImageUploading, setEditorImageUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [coverModerationState, setCoverModerationState] = useState<'scanning' | 'passed' | 'held' | 'blocked' | null>(null)
  const [coverModerationMessage, setCoverModerationMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [walletPixeBalance, setWalletPixeBalance] = useState<string>('0')
  const [insufficientPixeGate, setInsufficientPixeGate] = useState<{
    required_pixe: string
    current_pixe: string
    shortfall_pixe: string
    top_up_url: string
  } | null>(null)
  const [pubOpen, setPubOpen] = useState(false)
  const [creatorSubSettings, setCreatorSubSettings] = useState<CreatorSubscriptionSettings | null>(null)
  const [creatorTiers, setCreatorTiers] = useState<CreatorTier[] | null>(null)
  const [publishSettings, setPublishSettings] = useState<EditorPublishSettings>(() => {
    try {
      const raw = localStorage.getItem('draft:publish_settings')
      return raw ? { ...DEFAULT_EDITOR_PUBLISH_SETTINGS, ...JSON.parse(raw) } : { ...DEFAULT_EDITOR_PUBLISH_SETTINGS }
    } catch {
      return { ...DEFAULT_EDITOR_PUBLISH_SETTINGS }
    }
  })
  const ai = useAIEditor()
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  const [chatInput, setChatInput] = useState('')
  const chatRef = useRef<HTMLTextAreaElement | null>(null)
  const chatListRef = useRef<HTMLDivElement | null>(null)
  const chatEndRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = chatRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [chatInput])
  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, ai.loading])
  const [showHelp, setShowHelp] = useState(false)
  // AI modes + memory
  const [aiMode, setAiMode] = useState<'brainstorm' | 'editor' | 'story'>('brainstorm')
  const [aiSummary, setAiSummary] = useState<string>('')
  const [autoAppendStory, setAutoAppendStory] = useState<boolean>(true)
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
  const [serverDraftId, setServerDraftId] = useState<number | null>(null)
  const [serverDraftMeta, setServerDraftMeta] = useState<any | null>(null)
  const hydratedServerDraftIdRef = useRef<number | string | null>(null)
  const [draftsList, setDraftsList] = useState<Array<{ id: string; title: string; updated_at: string }>>([])
  const accessStorageKey = useCallback((draftId: number, field: 'access_level' | 'ppv_price_pixe' | 'required_tier_rank') => {
    return `draft:${draftId}:${field}`
  }, [])
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(() => {
    try {
      const storedServerId = Number((localStorage.getItem('drafts:server_id') || '').trim())
      const raw = (
        (storedServerId ? localStorage.getItem(`draft:${storedServerId}:access_level`) : null) ||
        localStorage.getItem('draft:access_level') || // legacy
        ''
      ).trim()
      if (raw === 'FREE' || raw === 'SUBSCRIBERS_ONLY' || raw === 'PAY_PER_VIEW' || raw === 'SUBSCRIBERS_OR_PPV') return raw
    } catch {}
    return 'FREE'
  })
  const [ppvPrice, setPpvPrice] = useState<string>(() => {
    try {
      const storedServerId = Number((localStorage.getItem('drafts:server_id') || '').trim())
      const raw = (
        (storedServerId ? localStorage.getItem(`draft:${storedServerId}:ppv_price_pixe`) : null) ||
        localStorage.getItem('draft:ppv_price_pixe') || // legacy
        ''
      ).trim()
      return raw
    } catch {
      return ''
    }
  })
  const [requiredTierRank, setRequiredTierRank] = useState<number | null>(() => {
    try {
      const storedServerId = Number((localStorage.getItem('drafts:server_id') || '').trim())
      const raw = (
        (storedServerId ? localStorage.getItem(`draft:${storedServerId}:required_tier_rank`) : null) ||
        ''
      ).trim()
      if (!raw) return null
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : null
    } catch {
      return null
    }
  })
  const [isLocalPost, setIsLocalPost] = useState<boolean>(false)
  const [localCity, setLocalCity] = useState<string>('')
  const [localLat, setLocalLat] = useState<string>('')
  const [localLng, setLocalLng] = useState<string>('')
  const [localDistance, setLocalDistance] = useState<LocalDistanceOption>(25)
  const [localTargetMode, setLocalTargetMode] = useState<LocalTargetMode>('profile_city')
  const [focusCustomCityInput, setFocusCustomCityInput] = useState(false)
  const [eventStartsAt, setEventStartsAt] = useState<string>('')
  const [eventEndsAt, setEventEndsAt] = useState<string>('')
  const [eventVenueName, setEventVenueName] = useState<string>('')
  const [eventAddress, setEventAddress] = useState<string>('')
  const [eventIsTicketed, setEventIsTicketed] = useState<boolean>(false)
  const [eventTicketPricePixe, setEventTicketPricePixe] = useState<string>('')
  const [eventCapacity, setEventCapacity] = useState<string>('')
  const [jobKind, setJobKind] = useState<'CONTRACT' | 'EMPLOYMENT'>('CONTRACT')
  const [jobVisibility, setJobVisibility] = useState<'OPEN' | 'DIRECT'>('OPEN')
  const [jobTargetProUser, setJobTargetProUser] = useState<string>('')
  const [jobSummary, setJobSummary] = useState<string>('')
  const [jobDetails, setJobDetails] = useState<string>('')
  const [jobLocationMode, setJobLocationMode] = useState<'REMOTE' | 'ONSITE' | 'HYBRID'>('REMOTE')
  const [jobLocationText, setJobLocationText] = useState<string>('')
  const [jobCompensationMode, setJobCompensationMode] = useState<'FIXED' | 'HOURLY' | 'SALARY'>('HOURLY')
  const [jobMinRate, setJobMinRate] = useState<string>('')
  const [jobMaxRate, setJobMaxRate] = useState<string>('')
  const [jobFixedPrice, setJobFixedPrice] = useState<string>('')
  const [jobSalary, setJobSalary] = useState<string>('')
  const [jobSkills, setJobSkills] = useState<string>('')
  // Cover AI
  const [coverAiOpen, setCoverAiOpen] = useState(false)
  const [coverAiPrompt, setCoverAiPrompt] = useState('')
  const [coverAiLoading, setCoverAiLoading] = useState(false)
  const [coverAiError, setCoverAiError] = useState<string | null>(null)
  const [coverAiPreview, setCoverAiPreview] = useState<string | null>(null)
  const [coverStatus, setCoverStatus] = useState<string | null>(null)
  const [lastCoverPrompt, setLastCoverPrompt] = useState<string | null>(null)
  const [titleStatus, setTitleStatus] = useState<string | null>(null)
  const [subtitleStatus, setSubtitleStatus] = useState<string | null>(null)
  const [serverDraftState, setServerDraftState] = useState<{ loaded: boolean; hasDraft: boolean }>({ loaded: !authed, hasDraft: false })

  const clearServerDraftId = () => {
    setServerDraftId(null)
    try { localStorage.removeItem('drafts:server_id') } catch {}
  }
  const removeLocalDraftRecord = (id: string | null) => {
    if (!id) return
    try {
      localStorage.removeItem(`drafts:item:${id}`)
      const idx = loadDraftsIndex().filter((d) => d.id !== id)
      saveDraftsIndex(idx)
      setDraftsList(idx)
    } catch {}
  }
  const clearLocalDraftStorage = () => {
    try {
      localStorage.removeItem('draft:title')
      localStorage.removeItem('draft:subtitle')
      localStorage.removeItem('draft:format')
      localStorage.removeItem('draft:category')
      localStorage.removeItem('draft:micro_text')
      localStorage.removeItem('draft:micro_template_id')
      localStorage.removeItem('draft:micro_advanced_open')
      localStorage.removeItem('draft:cover')
      localStorage.removeItem('draft:cover_landscape')
      localStorage.removeItem('draft:html')
      localStorage.removeItem('draft:cover_ai_prompt')
      // Legacy access keys (kept for migration only).
      localStorage.removeItem('draft:access_level')
      localStorage.removeItem('draft:ppv_price_pixe')
      localStorage.removeItem('drafts:current_id')
    } catch {}
  }
  const markJustPublished = () => {
    try { localStorage.setItem('drafts:just_published', '1') } catch {}
  }
  const resetEditorToBlank = () => {
    const blank = '<p></p>'
    setTitle('')
    setSubtitle('')
    setFormat('micro')
    setCategory('')
    setMicroText('')
    setMicroTemplateId(MICRO_TEMPLATES[0].id)
    setMicroAdvancedOpen(false)
    setCoverUrl(undefined)
    setCoverLandscapeUrl(undefined)
    setCoverPreviewUrl(undefined)
    setCoverLandscapePreviewUrl(undefined)
    setCoverModerationState(null)
    setCoverModerationMessage(null)
    setAccessLevel('FREE')
    setPpvPrice('')
    setRequiredTierRank(null)
    setIsLocalPost(false)
    setLocalCity('')
    setLocalLat('')
    setLocalLng('')
    setLocalDistance(25)
    setLocalTargetMode('profile_city')
    setEventStartsAt('')
    setEventEndsAt('')
    setEventVenueName('')
    setEventAddress('')
    setEventIsTicketed(false)
    setEventTicketPricePixe('')
    setEventCapacity('')
    setJobKind('CONTRACT')
    setJobVisibility('OPEN')
    setJobTargetProUser('')
    setJobSummary('')
    setJobDetails('')
    setJobLocationMode('REMOTE')
    setJobLocationText('')
    setJobCompensationMode('HOURLY')
    setJobMinRate('')
    setJobMaxRate('')
    setJobFixedPrice('')
    setJobSalary('')
    setJobSkills('')
    setHtml(blank)
    setBody('')
    setWordCount(0)
    setDirty(false)
    setLastSavedAt(null)
    setPreviewTab('auto')
    setCurrentDraftId(null)
    hydratedServerDraftIdRef.current = null
    try { richRef.current?.setHTML(blank) } catch {}
  }
  const resetAfterPublish = () => {
    removeLocalDraftRecord(currentDraftId)
    clearLocalDraftStorage()
    clearServerDraftId()
    resetEditorToBlank()
  }

  // Rich editor
  const richRef = useRef<RichEditorHandle | null>(null)
  const editorImageInputRef = useRef<HTMLInputElement | null>(null)
  const bootstrappedRef = useRef(false)
  const [html, setHtml] = useState<string>('')
  const [wordCount, setWordCount] = useState<number>(0)
  const [dirty, setDirty] = useState<boolean>(false)
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  const [previewTab, setPreviewTab] = useState<'auto' | 'portrait' | 'landscape'>('auto')
  const renderCoverUrl = coverPreviewUrl || coverUrl
  const renderCoverLandscapeUrl = coverLandscapePreviewUrl || coverLandscapeUrl
  const previewImage = useMemo(() => {
    if (previewTab === 'portrait') return renderCoverUrl || renderCoverLandscapeUrl
    if (previewTab === 'landscape') return renderCoverLandscapeUrl || renderCoverUrl
    return renderCoverLandscapeUrl || renderCoverUrl
  }, [previewTab, renderCoverUrl, renderCoverLandscapeUrl])
  const [tb, setTb] = useState<EditorToolbarState>({
    bold: false, italic: false, underline: false, strike: false, code: false, highlight: false, link: false,
    h1: false, h2: false, h3: false, h4: false, bullet: false, ordered: false, quote: false, codeBlock: false,
    align: 'left', canUndo: false, canRedo: false,
  })
  const [linkComposerOpen, setLinkComposerOpen] = useState(false)
  const [linkComposerUrl, setLinkComposerUrl] = useState('')
  const [linkComposerText, setLinkComposerText] = useState('')
  const [linkComposerMode, setLinkComposerMode] = useState<'text' | 'image'>('text')

  // Template preset (from /publish/new?template=...)
  const template = useMemo(() => (searchParams.get('template') || '').toLowerCase(), [searchParams])
  const requestedFormat = useMemo(() => (searchParams.get('format') || '').toLowerCase(), [searchParams])
  const requestedJobKind = useMemo(() => (searchParams.get('job_kind') || '').toUpperCase(), [searchParams])
  const requestedJobVisibility = useMemo(() => (searchParams.get('job_visibility') || '').toUpperCase(), [searchParams])
  const requestedTargetProUser = useMemo(() => (searchParams.get('target_pro_user') || '').trim(), [searchParams])
  const requestedTitle = useMemo(() => (searchParams.get('title') || '').trim(), [searchParams])
  const [freshIntent] = useState(() => {
    try {
      const v = sessionStorage.getItem('publish:fresh') === '1'
      if (v) sessionStorage.removeItem('publish:fresh')
      return v
    } catch {
      return false
    }
  })
  const fresh = useMemo(() => searchParams.get('fresh') === '1' || freshIntent, [searchParams, freshIntent])
  const handledFreshRef = useRef(false)
  useEffect(() => {
    if (!fresh || handledFreshRef.current) return
    handledFreshRef.current = true
    try { localStorage.removeItem('drafts:just_published') } catch {}
    clearLocalDraftStorage()
    clearServerDraftId()
    setCurrentDraftId(null)
    resetEditorToBlank()
  }, [fresh])
  useEffect(() => {
    if (requestedFormat !== 'job') return
    setFormat('job')
    if (requestedJobKind === 'CONTRACT' || requestedJobKind === 'EMPLOYMENT') {
      setJobKind(requestedJobKind)
    }
    if (requestedJobVisibility === 'OPEN' || requestedJobVisibility === 'DIRECT') {
      setJobVisibility(requestedJobVisibility)
    }
    if (requestedTargetProUser) setJobTargetProUser(requestedTargetProUser)
    if (requestedTitle) setTitle((prev) => prev || requestedTitle)
  }, [requestedFormat, requestedJobKind, requestedJobVisibility, requestedTargetProUser, requestedTitle])

  useEffect(() => {
    if (!template) return
    if (template === 'micro') {
      setTitle((t) => t || 'A quick update')
      setSubtitle((s) => s || 'Short and sweet')
      setBody((b) => b || "Write a few lines to share what's new...")
      setHtml((h) => h || "<p>Write a few lines to share what's new...</p>")
    } else if (template === 'zine') {
      setTitle((t) => t || "A visual zine on ...")
      setSubtitle((s) => s || 'Stories, images, and quotes')
      setBody((b) => b || `Section 1\nIntro...\n\nPull quote...\n\nSection 2\nMore details...`)
      setHtml((h) => h || "<h2>Section 1</h2><p>Intro...</p><blockquote>Pull quote...</blockquote><h3>Section 2</h3><p>More details...</p>")
    } else if (template === 'book') {
      setTitle((t) => t || "The Guide to ...")
      setSubtitle((s) => s || 'Chapters and deep dives')
      setBody((b) => b || `Chapter 1\n\nChapter 2\n\nChapter 3`)
      setHtml((h) => h || "<h2>Chapter 1</h2><p>...</p><h2>Chapter 2</h2><p>...</p><h2>Chapter 3</h2><p>...</p>")
    } else if (template === 'photo') {
      setTitle((t) => t || "Photo set: ...")
      setSubtitle((s) => s || 'A small collection of images')
      setBody((b) => b || 'Add a cover, then captions under each image.')
      setHtml((h) => h || '<p>Add a cover, then captions under each image.</p>')
    } else if (template === 'launch') {
      setTitle((t) => t || "Launching ...")
      setSubtitle((s) => s || 'What it is, why it matters')
      setBody((b) => b || 'Describe your drop, add a CTA, and link the store item.')
      setHtml((h) => h || '<p>Describe your drop, add a CTA, and link the store item.</p>')
    }
  }, [template])

  // After template/draft sets html, inject it once into the editor DOM without
  // controlling it on every render (prevents caret jumping/reverse typing).
  useEffect(() => {
    if (bootstrappedRef.current) return
    if (html && richRef.current) {
      richRef.current.setHTML(html)
      bootstrappedRef.current = true
    }
  }, [html])

  // Keep a running word count from plain body text
  useEffect(() => {
    try { setWordCount(countWords(body || '')) } catch { setWordCount(0) }
  }, [body])

  // Auto-save draft
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem('draft:title', title)
        localStorage.setItem('draft:subtitle', subtitle)
        localStorage.setItem('draft:format', format)
        localStorage.setItem('draft:category', category)
        localStorage.setItem('draft:micro_text', microText)
        localStorage.setItem('draft:micro_template_id', microTemplateId)
        localStorage.setItem('draft:micro_advanced_open', microAdvancedOpen ? '1' : '0')
        localStorage.setItem('draft:cover', coverUrl || '')
        localStorage.setItem('draft:cover_landscape', coverLandscapeUrl || '')
        localStorage.setItem('draft:html', html)
        // Access is per-draft to avoid cross-draft contamination. Unsaved drafts stay in-memory only.
        if (serverDraftId) {
          localStorage.setItem(accessStorageKey(serverDraftId, 'access_level'), accessLevel)
          localStorage.setItem(accessStorageKey(serverDraftId, 'ppv_price_pixe'), ppvPrice)
          localStorage.setItem(accessStorageKey(serverDraftId, 'required_tier_rank'), requiredTierRank ? String(requiredTierRank) : '')
        }
        localStorage.setItem('draft:publish_settings', JSON.stringify(publishSettings))
        setLastSavedAt(Date.now())
        setDirty(false)
      } catch {}
    }, 500)
    return () => clearTimeout(t)
  }, [title, subtitle, format, category, microText, microTemplateId, microAdvancedOpen, coverUrl, coverLandscapeUrl, html, accessLevel, ppvPrice, requiredTierRank, publishSettings, serverDraftId, accessStorageKey])

  useEffect(() => {
    if (!serverDraftId) return
    // One-way migration from legacy global keys into per-draft keys.
    try {
      const legacyAccess = (localStorage.getItem('draft:access_level') || '').trim()
      const legacyPpv = (localStorage.getItem('draft:ppv_price_pixe') || '').trim()
      const keyA = accessStorageKey(serverDraftId, 'access_level')
      const keyP = accessStorageKey(serverDraftId, 'ppv_price_pixe')
      if (legacyAccess && !localStorage.getItem(keyA)) localStorage.setItem(keyA, legacyAccess)
      if (legacyPpv && !localStorage.getItem(keyP)) localStorage.setItem(keyP, legacyPpv)
    } catch {}
  }, [serverDraftId, accessStorageKey])

  useEffect(() => {
    try {
      localStorage.setItem('draft:cover_landscape', coverLandscapeUrl || '')
    } catch {}
  }, [coverLandscapeUrl])

  const restoredRef = useRef(false)
  useEffect(() => {
    if (!serverDraftState.loaded || restoredRef.current) return
    restoredRef.current = true
    try {
      if (fresh) {
        clearLocalDraftStorage()
        clearServerDraftId()
        resetEditorToBlank()
        return
      }
      const justPublished = localStorage.getItem('drafts:just_published') === '1'
      if (justPublished) {
        localStorage.removeItem('drafts:just_published')
        clearLocalDraftStorage()
        clearServerDraftId()
        resetEditorToBlank()
        return
      }
      if (!serverDraftState.hasDraft) {
        clearLocalDraftStorage()
        clearServerDraftId()
        resetEditorToBlank()
        return
      }
      // Server draft exists; state will be restored from DB.
      return
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDraftState.loaded])

  useEffect(() => {
    let cancelled = false
    if (!authed) {
      setServerDraftState({ loaded: true, hasDraft: false })
      return
    }
    async function load() {
      try {
        if (fresh) {
          if (!cancelled) {
            setServerDraftState({ loaded: true, hasDraft: false })
            setServerDraftId(null)
            setServerDraftMeta(null)
          }
          return
        }
        const res = await listServerDrafts()
        const drafts = Array.isArray(res?.results) ? res.results : res || []
        const sorted = [...drafts].sort((a: any, b: any) => {
          const at = Date.parse(a?.updated_at || a?.created_at || 0) || 0
          const bt = Date.parse(b?.updated_at || b?.created_at || 0) || 0
          if (at !== bt) return bt - at
          return (b?.id || 0) - (a?.id || 0)
        })
        const latest = sorted[0]
        if (!cancelled) {
          setServerDraftState({ loaded: true, hasDraft: drafts.length > 0 })
          if (latest?.id) {
            setServerDraftId(latest.id)
            setServerDraftMeta(latest)
            try { localStorage.setItem('drafts:server_id', String(latest.id)) } catch {}
          } else {
            setServerDraftMeta(null)
          }
          const stored = localStorage.getItem('drafts:server_id')
          if (stored && drafts.every((d: any) => String(d?.id) !== stored)) clearServerDraftId()
        }
      } catch {
        if (!cancelled) setServerDraftState({ loaded: true, hasDraft: false })
      }
    }
    setServerDraftState((prev) => ({ ...prev, loaded: false }))
    load()
    return () => { cancelled = true }
  }, [authed, fresh])

  useEffect(() => {
    let cancelled = false
    if (!authed || !serverDraftId) return
    async function loadDraftDetail() {
      try {
        const detail = await getDraft(serverDraftId)
        if (cancelled) return
        setServerDraftMeta((prev: any) => ({ ...prev, ...detail }))
      } catch {}
    }
    loadDraftDetail()
    return () => { cancelled = true }
  }, [authed, serverDraftId])

  useEffect(() => {
    if (fresh) return
    if (!authed || !serverDraftMeta) return
    const hydrateId = serverDraftMeta.id ?? serverDraftId
    if (hydrateId != null && hydratedServerDraftIdRef.current === hydrateId) return
    clearLocalDraftStorage()
    setCurrentDraftId(null)
    setTitle(serverDraftMeta.title || '')
    setSubtitle(serverDraftMeta.subtitle || '')
    setFormat((serverDraftMeta.format as any) || 'micro')
    setCategory((serverDraftMeta.category as any) || '')
    setMicroText(serverDraftMeta.micro_text || '')
    setMicroTemplateId(serverDraftMeta.micro_template_id || MICRO_TEMPLATES[0].id)
    const hydratedCoverUrl = serverDraftMeta.cover_image_url || ''
    const hydratedLandscapeUrl = serverDraftMeta.cover_landscape_url || ''
    if (!coverPreviewUrl) setCoverUrl(hydratedCoverUrl)
    if (!coverLandscapePreviewUrl) setCoverLandscapeUrl(hydratedLandscapeUrl)
    setAccessLevel((serverDraftMeta.access_level as AccessLevel) || 'FREE')
    setPpvPrice(serverDraftMeta.ppv_price_pixe ? String(serverDraftMeta.ppv_price_pixe) : '')
    setRequiredTierRank(serverDraftMeta.required_tier_rank ? Number(serverDraftMeta.required_tier_rank) : null)
    setIsLocalPost(Boolean(serverDraftMeta.is_local))
    {
      const city = String(serverDraftMeta.local_city || '').trim()
      const region = String(serverDraftMeta.local_region || '').trim()
      const country = String(serverDraftMeta.local_country || '').trim()
      const label = city ? [city, region || country].filter(Boolean).join(', ') : ''
      setLocalCity(label || city)
    }
    setLocalLat(serverDraftMeta.local_lat != null ? String(serverDraftMeta.local_lat) : '')
    setLocalLng(serverDraftMeta.local_lng != null ? String(serverDraftMeta.local_lng) : '')
    setLocalDistance(kmToDistancePreset(serverDraftMeta.local_radius_km))
    if (serverDraftMeta.local_lat != null && serverDraftMeta.local_lng != null) setLocalTargetMode('near_me')
    else if (serverDraftMeta.local_city) setLocalTargetMode('custom_city')
    else setLocalTargetMode('profile_city')
    setEventStartsAt(serverDraftMeta.event_starts_at ? String(serverDraftMeta.event_starts_at) : '')
    setEventEndsAt(serverDraftMeta.event_ends_at ? String(serverDraftMeta.event_ends_at) : '')
    setEventVenueName(String(serverDraftMeta.event_venue_name || ''))
    setEventAddress(String(serverDraftMeta.event_address || ''))
    setEventIsTicketed(Boolean(serverDraftMeta.event_is_ticketed))
    setEventTicketPricePixe(serverDraftMeta.event_ticket_price_pixe != null ? String(serverDraftMeta.event_ticket_price_pixe) : '')
    setEventCapacity(serverDraftMeta.event_capacity != null ? String(serverDraftMeta.event_capacity) : '')
    const draftHtml = serverDraftMeta.body_html || serverDraftMeta.body || ''
    const draftPlain = serverDraftMeta.body_plain || ''
    if (draftHtml) {
      setHtml(draftHtml)
      setBody(editorTextFromHtml(draftHtml))
    } else if (draftPlain) {
      const h = textToHtmlParagraphs(draftPlain)
      setHtml(h)
      setBody(draftPlain)
    }
    if (hydrateId != null) hydratedServerDraftIdRef.current = hydrateId
  }, [authed, serverDraftMeta, fresh, serverDraftId, coverPreviewUrl, coverLandscapePreviewUrl])

  useEffect(() => {
    let cancelled = false
    if (!authed) return
    ;(async () => {
      try {
        const res = await listMyCreatorTiers()
        if (!cancelled) setCreatorTiers(res?.results || [])
      } catch {
        if (!cancelled) setCreatorTiers(null)
      }
    })()
    return () => { cancelled = true }
  }, [authed])

  useEffect(() => {
    if (localTargetMode !== 'profile_city') return
    if (!profileCityLabel) return
    if (!String(localCity || '').trim()) setLocalCity(profileCityLabel)
  }, [localTargetMode, profileCityLabel, localCity])

  useEffect(() => {
    if (localTargetMode === 'near_me') return
    if (localLat || localLng) {
      setLocalLat('')
      setLocalLng('')
    }
  }, [localTargetMode, localLat, localLng])

  useEffect(() => {
    let cancelled = false
    if (!authed) return
    ;(async () => {
      try {
        const s = await getMyCreatorSubscriptionSettings()
        if (!cancelled) setCreatorSubSettings(s)
      } catch {
        if (!cancelled) setCreatorSubSettings(null)
      }
    })()
    return () => { cancelled = true }
  }, [authed])

  // Unsaved changes guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (title || subtitle || html) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [title, subtitle, html])

  async function prepareImageFile(orig: File): Promise<{ file: File; ctype: string }> {
    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp'])
    let file = orig
    let ctype = file.type || 'application/octet-stream'

    // If browser reports an unsupported type (e.g., HEIC) but can decode it,
    // try to transcode to JPEG via canvas.
    if (!allowed.has(ctype) && ctype.startsWith('image/')) {
      try {
        const transcoded = await transcodeToJpeg(file)
        if (transcoded) {
          file = transcoded
          ctype = 'image/jpeg'
        }
      } catch {}
    }

    if (!allowed.has(ctype)) {
      throw new Error('Unsupported image type. Use JPG, PNG, or WebP.')
    }

    return { file, ctype }
  }

  async function uploadCoverFile(
    file: File,
    contentType?: string,
    targetField: 'cover_image_url' | 'cover_landscape_url' = 'cover_image_url',
  ): Promise<{ url: string; previewUrl: string; moderationState: 'passed' | 'held' | 'blocked' | 'scanning' }> {
    const resolvePending = async (checkId?: string): Promise<'passed' | 'held' | 'blocked'> => {
      if (!checkId) return 'held'
      for (let i = 0; i < 8; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 500))
        try {
          const check = await getModerationCheckStatus(checkId)
          const status = String(check?.status || 'pending').toLowerCase()
          if (status === 'passed') return 'passed'
          if (status === 'blocked') return 'blocked'
          if (status === 'held' || status === 'error') return 'held'
        } catch {
          return 'held'
        }
      }
      return 'held'
    }

    const ctype = contentType || file.type || 'application/octet-stream'
    try {
      // Prefer server-side direct upload first to avoid browser->Spaces CORS preflight failures.
      const direct = await uploadCoverDirect(file, {
        upload_content_type: 'post_image',
        target_field: targetField,
        object_type: 'posts.upload',
      })
      const moderationStatus = String(direct?.moderation_status || 'passed').toLowerCase()
      const publicUrl = String(direct?.public_url || '').trim()
      const previewUrl = String(direct?.render_url || direct?.preview_url || publicUrl || '').trim()
      if (moderationStatus === 'pending') {
        const polled = await resolvePending(direct?.check_id)
        return { url: publicUrl, previewUrl: previewUrl || publicUrl, moderationState: polled }
      }
      if (moderationStatus === 'blocked') {
        return { url: publicUrl, previewUrl: previewUrl || publicUrl, moderationState: 'blocked' }
      }
      if (moderationStatus === 'held' || moderationStatus === 'error' || moderationStatus === 'pending') {
        return { url: publicUrl, previewUrl: previewUrl || publicUrl, moderationState: 'held' }
      }
      return { url: publicUrl, previewUrl: previewUrl || publicUrl, moderationState: 'passed' }
    } catch {
      // Fallback to presigned PUT path.
      const presign = await presignCover(file.name, ctype)
      await uploadToSignedUrl(presign.upload_url, file, presign.content_type)
      const finalized = await finalizePresignedCoverUpload({
        public_url: presign.public_url,
        key: presign.key,
        content_type: presign.content_type,
        upload_content_type: 'post_image',
        target_field: targetField,
        object_type: 'posts.upload',
      })
      const moderationStatus = String(finalized?.moderation_status || 'passed').toLowerCase()
      const previewUrl = String(finalized?.preview_url || presign.public_url || '').trim()
      if (moderationStatus === 'pending') {
        const polled = await resolvePending(finalized?.check_id)
        return { url: presign.public_url, previewUrl: previewUrl || presign.public_url, moderationState: polled }
      }
      if (moderationStatus === 'blocked') {
        return { url: presign.public_url, previewUrl: previewUrl || presign.public_url, moderationState: 'blocked' }
      }
      if (moderationStatus === 'held' || moderationStatus === 'error' || moderationStatus === 'pending') {
        return { url: presign.public_url, previewUrl: previewUrl || presign.public_url, moderationState: 'held' }
      }
      return { url: presign.public_url, previewUrl: previewUrl || presign.public_url, moderationState: 'passed' }
    }
  }

  async function handlePortraitFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const orig = e.target.files?.[0]
    if (!orig) return
    setError(null)
    setUploadError(null)
    setCoverModerationState('scanning')
    setCoverModerationMessage('Scanning image...')
    setUploading(true)
    let localUrl: string | null = null
    try {
      const { file, ctype } = await prepareImageFile(orig)
      localUrl = URL.createObjectURL(file)
      setCoverUrl(localUrl)
      setCoverPreviewUrl(localUrl)
      const upload = await uploadCoverFile(file, ctype, 'cover_image_url')
      if (upload.moderationState === 'blocked') {
        setCoverModerationState('blocked')
        setCoverModerationMessage("This image can't be posted. Please choose another image.")
        throw new Error("This image can't be posted.")
      }
      if (upload.moderationState === 'held') {
        setCoverModerationState('held')
        setCoverModerationMessage('Submitted for review. You can keep drafting, but this cannot publish publicly yet.')
      } else if (upload.moderationState === 'scanning') {
        setCoverModerationState('scanning')
        setCoverModerationMessage('Upload complete. Image scan continues at publish time.')
      } else {
        setCoverModerationState('passed')
        setCoverModerationMessage('Image scan passed.')
      }
      const publicUrl = upload.url
      setCoverUrl(publicUrl)
      setCoverPreviewUrl(upload.previewUrl || publicUrl)
      if (publishSettings.autoCropLandscape && !coverLandscapeUrl) {
        await autoCropLandscape(file).catch(() => null)
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Upload failed'
      setError(msg)
      setUploadError(msg)
      console.error('Portrait cover upload error:', e)
    } finally {
      if (localUrl) URL.revokeObjectURL(localUrl)
      setUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  async function handleLandscapeFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const orig = e.target.files?.[0]
    if (!orig) return
    setError(null)
    setUploadError(null)
    setCoverModerationState('scanning')
    setCoverModerationMessage('Scanning image...')
    setUploading(true)
    let localUrl: string | null = null
    try {
      const { file, ctype } = await prepareImageFile(orig)
      localUrl = URL.createObjectURL(file)
      setCoverLandscapeUrl(localUrl)
      setCoverLandscapePreviewUrl(localUrl)
      const upload = await uploadCoverFile(file, ctype, 'cover_landscape_url')
      if (upload.moderationState === 'blocked') {
        setCoverModerationState('blocked')
        setCoverModerationMessage("This image can't be posted. Please choose another image.")
        throw new Error("This image can't be posted.")
      }
      if (upload.moderationState === 'held') {
        setCoverModerationState('held')
        setCoverModerationMessage('Submitted for review. You can keep drafting, but this cannot publish publicly yet.')
      } else if (upload.moderationState === 'scanning') {
        setCoverModerationState('scanning')
        setCoverModerationMessage('Upload complete. Image scan continues at publish time.')
      } else {
        setCoverModerationState('passed')
        setCoverModerationMessage('Image scan passed.')
      }
      setCoverLandscapeUrl(upload.url)
      setCoverLandscapePreviewUrl(upload.previewUrl || upload.url)
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Upload failed'
      setError(msg)
      setUploadError(msg)
      console.error('Landscape cover upload error:', e)
    } finally {
      if (localUrl) URL.revokeObjectURL(localUrl)
      setUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  async function generateCroppedVariant(options: {
    source?: string | File | null
    ratio: number
    width: number
    height: number
    filename: string
    targetField: 'cover_image_url' | 'cover_landscape_url'
    onDone: (url: string, previewUrl?: string) => void
  }) {
    try {
      let tempUrl: string | null = null
      let targetSrc: string | null = null
      if (options.source instanceof File) {
        tempUrl = URL.createObjectURL(options.source)
        targetSrc = tempUrl
      } else {
        targetSrc = options.source || null
      }
      if (!targetSrc) return
      const img: HTMLImageElement = await new Promise((resolve, reject) => {
        const i = new Image()
        i.crossOrigin = 'anonymous'
        i.onload = () => resolve(i)
        i.onerror = reject
        i.src = targetSrc
      })
      const sw = img.naturalWidth
      const sh = img.naturalHeight
      if (!sw || !sh) return
      const srcRatio = sw / sh
      let cw: number
      let ch: number
      if (srcRatio > options.ratio) {
        ch = sh
        cw = Math.round(sh * options.ratio)
      } else {
        cw = sw
        ch = Math.round(sw / options.ratio)
      }
      const sx = Math.max(0, Math.floor((sw - cw) / 2))
      const sy = Math.max(0, Math.floor((sh - ch) / 2))
      const canvas = document.createElement('canvas')
      canvas.width = options.width
      canvas.height = options.height
      const ctx = canvas.getContext('2d')!
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, sx, sy, cw, ch, 0, 0, canvas.width, canvas.height)
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
      if (!blob) return
      // @ts-ignore
      const file = new File([blob], options.filename, { type: 'image/jpeg' })
      const upload = await uploadCoverFile(file, 'image/jpeg', options.targetField)
      options.onDone(upload.url, upload.previewUrl)
      if (tempUrl) URL.revokeObjectURL(tempUrl)
    } catch (e) {
      console.error('Auto-crop failed', e)
    }
  }

  async function autoCropLandscape(source?: string | File | null) {
    await generateCroppedVariant({
      source: source || coverUrl || null,
      ratio: 16 / 9,
      width: 1600,
      height: 900,
      filename: 'cover-landscape.jpg',
      targetField: 'cover_landscape_url',
      onDone: (url, previewUrl) => {
        setCoverLandscapeUrl(url)
        setCoverLandscapePreviewUrl(previewUrl || url)
      },
    })
  }

  async function autoCropPortrait(source?: string | File | null) {
    await generateCroppedVariant({
      source: source || coverLandscapeUrl || null,
      ratio: 3 / 4,
      width: 1200,
      height: 1600,
      filename: 'cover-portrait.jpg',
      targetField: 'cover_image_url',
      onDone: (url, previewUrl) => {
        setCoverUrl(url)
        setCoverPreviewUrl(previewUrl || url)
      },
    })
  }

  function onRemovePortrait() {
    setCoverUrl(undefined)
    setCoverPreviewUrl(undefined)
    setCoverModerationState(null)
    setCoverModerationMessage(null)
    setUploadError(null)
    setDirty(true)
    try {
      localStorage.setItem('draft:cover', '')
    } catch {}
  }

  function onRemoveLandscape() {
    setCoverLandscapeUrl(undefined)
    setCoverLandscapePreviewUrl(undefined)
    setCoverModerationState(null)
    setCoverModerationMessage(null)
    setUploadError(null)
    setDirty(true)
    try { localStorage.setItem('draft:cover_landscape', '') } catch {}
  }

  function confirmOverwrite(label: string) {
    return window.confirm(`Replace existing ${label} cover? This will overwrite the current image.`)
  }

  const subsEnabled = Boolean(creatorSubSettings?.is_enabled)
  const ppvAllowed = Boolean(creatorSubSettings?.allow_pay_per_view)
  const defaultPpv = creatorSubSettings?.default_ppv_price_pixe || 1
  const hasTiers = Boolean((creatorTiers || []).filter((t) => t.is_active).length > 0)

  useEffect(() => {
    const next = normalizeAccessSelection({ level: accessLevel, subsEnabled, ppvAllowed })
    if (next !== accessLevel) setAccessLevel(next)
    if (!subsEnabled) setRequiredTierRank(null)
    if (accessLevel === 'FREE' || accessLevel === 'PAY_PER_VIEW') setRequiredTierRank(null)
  }, [subsEnabled, ppvAllowed, accessLevel])

  const ppvInt = (() => {
    const v = (ppvPrice || '').trim()
    if (!v) return null
    if (!/^\d+$/.test(v)) return null
    const n = Number(v)
    if (!Number.isFinite(n) || n < 1) return null
    return n
  })()
  const parsedLocalLat = (() => {
    const raw = String(localLat || '').trim()
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const parsedLocalLng = (() => {
    const raw = String(localLng || '').trim()
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const localHasPoint = parsedLocalLat != null && parsedLocalLng != null
  const resolvedLocalCity = (() => {
    if (localTargetMode === 'profile_city') return String(localCity || profileCityLabel || '').trim()
    if (localTargetMode === 'custom_city') return String(localCity || '').trim()
    return String(localCity || '').trim()
  })()
  const localMissing = format !== 'job' && isLocalPost && (
    localTargetMode === 'near_me' ? !localHasPoint : !resolvedLocalCity
  )
  const eventTicketPriceInt = (() => {
    const raw = String(eventTicketPricePixe || '').trim()
    if (!raw) return null
    if (!/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const eventCapacityInt = (() => {
    const raw = String(eventCapacity || '').trim()
    if (!raw) return null
    if (!/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const jobTargetProUserId = (() => {
    const raw = String(jobTargetProUser || '').trim()
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
  })()
  const jobMinRateInt = (() => {
    const raw = String(jobMinRate || '').trim()
    if (!raw || !/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const jobMaxRateInt = (() => {
    const raw = String(jobMaxRate || '').trim()
    if (!raw || !/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const jobFixedPriceInt = (() => {
    const raw = String(jobFixedPrice || '').trim()
    if (!raw || !/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const jobSalaryInt = (() => {
    const raw = String(jobSalary || '').trim()
    if (!raw || !/^\d+$/.test(raw)) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  })()
  const eventStartsMissing = isEventStartMissing(format, eventStartsAt)
  const eventEndBeforeStart = isEventEndBeforeStart(format, eventStartsAt, eventEndsAt)
  const eventLocationMissing =
    format === 'event' &&
    (localTargetMode === 'near_me' ? !localHasPoint : !resolvedLocalCity)
  const eventTicketFieldsInvalid =
    format === 'event' &&
    eventIsTicketed &&
    ((eventTicketPriceInt || 0) < 1 || (eventCapacityInt || 0) < 1)
  const accessNeedsPrice = format !== 'job' && accessRequiresPPV(accessLevel)
  const accessPriceInvalid = accessNeedsPrice && (ppvInt === null || ppvInt < 1)
  const microMissing = format === 'micro' && !String(microText || '').trim()
  const jobDirectMissingTarget = format === 'job' && jobKind === 'CONTRACT' && jobVisibility === 'DIRECT' && !jobTargetProUserId
  const jobFixedMissingPrice = format === 'job' && jobCompensationMode === 'FIXED' && !jobFixedPriceInt
  const jobSalaryMissing = format === 'job' && jobCompensationMode === 'SALARY' && !jobSalaryInt
  const publishDisabledReason =
    coverModerationState === 'blocked'
      ? "This image can't be posted. Replace it before publishing."
      : coverModerationState === 'held'
      ? 'An image is under review. You can keep drafting but cannot publish publicly yet.'
      : accessPriceInvalid
      ? 'Set an unlock price (1+ PIXE) to publish.'
      : localMissing
      ? 'Local posts need a city label or your location.'
      : eventStartsMissing
      ? 'Event start date/time is required.'
      : eventEndBeforeStart
      ? 'End date/time cannot be before start.'
      : eventLocationMissing
      ? 'Events need a city label or your location.'
      : eventTicketFieldsInvalid
      ? 'Ticketed events need ticket price and capacity.'
      : microMissing
      ? 'Micro text is required to publish.'
      : jobDirectMissingTarget
      ? 'Direct contract jobs require a target Pro user id.'
      : jobFixedMissingPrice
      ? 'Fixed jobs require a fixed price (PIXE x100).'
      : jobSalaryMissing
      ? 'Salary jobs require a salary value.'
      : format !== 'job' && !category
      ? 'Select a category to publish.'
      : null
  const publishCostPixe = '1'
  const canAffordPublish = Number.parseFloat(walletPixeBalance || '0') >= Number.parseFloat(publishCostPixe)

  useEffect(() => {
    if (!authed) return
    let cancelled = false
    getWalletSummary()
      .then((wallet) => {
        if (cancelled) return
        setWalletPixeBalance(String(wallet?.summary?.pixe_balance || '0'))
      })
      .catch(() => {
        if (!cancelled) setWalletPixeBalance('0')
      })
    return () => {
      cancelled = true
    }
  }, [authed])

  function normalizeInsufficientPixePayload(data: any) {
    if (!data || data?.error !== 'insufficient_pixe') return null
    const required = String(data?.required_pixe || publishCostPixe || '1')
    const current = String(data?.current_pixe || walletPixeBalance || '0')
    const shortfall = String(data?.shortfall_pixe || Math.max(0, Number(required) - Number(current)))
    return {
      required_pixe: required,
      current_pixe: current,
      shortfall_pixe: shortfall,
      top_up_url: String(data?.top_up_url || '/settings/wallet'),
    }
  }

  function normalizeInsufficientNetworkFeePayload(data: any) {
    if (!data || data?.error !== 'insufficient_network_fee') return null
    return {
      detail:
        String(data?.detail || '').trim() ||
        'Your on-chain spendable balance is too low to cover publish/network fees.',
      top_up_url: String(data?.top_up_url || '/settings/wallet'),
      current_onchain_pixe: String(data?.current_onchain_pixe || '0'),
      required_onchain_pixe: String(data?.required_onchain_pixe || publishCostPixe || '1'),
    }
  }

  useEffect(() => {
    if (format !== 'micro') return
    // Publish requires a title, but micro mode hides it. Keep it deterministic.
    if (title.trim()) return
    const base = String(microText || '').trim()
    if (base) {
      setTitle(base.split(/\r?\n/)[0].slice(0, 80))
      return
    }
    setTitle('Micro')
  }, [format, title, microText])

  function onChangeMicroText(next: string) {
    setMicroText(next)
    setDirty(true)
  }

  function onPickAccess(next: AccessLevel) {
    if (isAccessOptionDisabled({ option: next, subsEnabled, ppvAllowed })) return
    setAccessLevel(next)
    if (accessRequiresPPV(next)) {
      const cur = (ppvPrice || '').trim()
      if (!cur) setPpvPrice(String(defaultPpv > 0 ? defaultPpv : 1))
    }
  }

  async function onUseMyLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not available in this browser.')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocalLat(String(Number(pos.coords.latitude).toFixed(6)))
        setLocalLng(String(Number(pos.coords.longitude).toFixed(6)))
        setLocalTargetMode('near_me')
        setFocusCustomCityInput(false)
        if (!isLocalPost) setIsLocalPost(true)
      },
      () => {
        const fallback = resolveGeoDeniedFallback(profileCityLabel)
        setLocalTargetMode(fallback.mode)
        setLocalCity(fallback.cityLabel)
        setFocusCustomCityInput(fallback.focusCustomCityInput)
        setLocalLat('')
        setLocalLng('')
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
    )
  }

  async function onGenerateLandscape() {
    if (!coverUrl) return
    if (coverLandscapeUrl && !confirmOverwrite('landscape')) return
    await autoCropLandscape(coverUrl)
  }

  async function onGeneratePortrait() {
    if (!coverLandscapeUrl) return
    if (coverUrl && !confirmOverwrite('portrait')) return
    await autoCropPortrait(coverLandscapeUrl)
  }

  function isQueuedPublish(post: { publish_status?: string; last_error?: string }) {
    if (post.publish_status !== 'pending') return false
    return (post.last_error || '').toLowerCase().startsWith('publish_queued')
  }

  function isPendingReviewPublish(post: { moderation_state?: string }) {
    return String(post?.moderation_state || '').toLowerCase() === 'pending_review'
  }

  async function onPublish() {
    if (!authed) { nav('/login'); return }
    if (publishDisabledReason) {
      showToast('WARN', 'Publish blocked', publishDisabledReason)
      setError(publishDisabledReason)
      return
    }
    if (format !== 'job' && !canAffordPublish) {
      setInsufficientPixeGate({
        required_pixe: publishCostPixe,
        current_pixe: walletPixeBalance || '0',
        shortfall_pixe: String(Math.max(0, Number(publishCostPixe) - Number(walletPixeBalance || '0'))),
        top_up_url: '/settings/wallet',
      })
      return
    }
    if (uploading) {
      showToast('INFO', 'Still uploading', 'Wait for cover upload to finish.')
      return
    }
    if (uploadError) {
      showToast('WARN', 'Cover upload failed', uploadError)
      setError(uploadError)
      return
    }
    setSaving(true)
    setError(null)
    if (format === 'job') {
      try {
        const skills = String(jobSkills || '')
          .split(',')
          .map((token) => token.trim())
          .filter(Boolean)
        const draft = await createJobDraft({
          job_kind: jobKind,
          job_visibility: jobKind === 'EMPLOYMENT' ? 'OPEN' : jobVisibility,
          target_pro_user: jobKind === 'CONTRACT' && jobVisibility === 'DIRECT' ? jobTargetProUserId : null,
          title: String(title || '').trim() || 'Untitled job',
          summary: String(jobSummary || '').trim(),
          details_md: String(jobDetails || '').trim(),
          location_mode: jobLocationMode,
          location_text: String(jobLocationText || '').trim(),
          compensation_mode: jobCompensationMode,
          min_rate_cents: jobCompensationMode === 'HOURLY' ? jobMinRateInt : null,
          max_rate_cents: jobCompensationMode === 'HOURLY' ? jobMaxRateInt : null,
          fixed_price_cents: jobCompensationMode === 'FIXED' ? jobFixedPriceInt : null,
          salary_cents: jobCompensationMode === 'SALARY' ? jobSalaryInt : null,
          currency: 'USD',
          skills,
        })
        const publishedJob = await publishJob(draft.id)
        showToast('OK', 'Job published', title || 'Your job is live')
        setTimeout(() => {
          resetAfterPublish()
          nav(`/jobs/${publishedJob.id}`)
        }, 150)
        return
      } catch (e: any) {
        const msg = e?.response?.data?.detail || e?.message || 'Failed to publish job'
        setError(msg)
        showToast('WARN', 'Publish failed', msg)
        return
      } finally {
        setSaving(false)
      }
    }
    const htmlContent = richRef.current?.getHTML() || html || ''
    const plain = editorTextFromHtml(htmlContent)
    const bodyPatchPayload = buildBodyPatchPayload(plain, htmlContent, 'publish')
    const safeCover = sanitizeCoverUrl(coverUrl) || undefined
    const safeCoverLandscape = sanitizeCoverUrl(coverLandscapeUrl) || undefined
    const publishAttemptKey = createPublishAttemptKey()
    const effectivePpv = accessNeedsPrice ? (ppvInt || null) : null
    const publishPayload = {
      title: title.trim(),
      subtitle: subtitle.trim() || undefined,
      format,
      category,
      micro_text: format === 'micro' ? String(microText || '').trim() : (microText ? String(microText) : undefined),
      micro_template_id: microTemplateId || undefined,
      cover_image_url: safeCover,
      cover_landscape_url: safeCoverLandscape,
      body_plain: plain || '.',
      access_level: accessLevel,
      ppv_price_pixe: effectivePpv,
      required_tier_rank: requiredTierRank || null,
      ...buildLocalPayload({
        enabled: isLocalPost,
        mode: localTargetMode,
        distance: localDistance,
        cityLabel: resolvedLocalCity,
        lat: parsedLocalLat,
        lng: parsedLocalLng,
      }),
      event_starts_at: format === 'event' ? (eventStartsAt || null) : null,
      event_ends_at: format === 'event' ? (eventEndsAt || null) : null,
      event_venue_name: format === 'event' ? String(eventVenueName || '').trim() : '',
      event_address: format === 'event' ? String(eventAddress || '').trim() : '',
      event_ticket_price_pixe: format === 'event' && eventIsTicketed ? eventTicketPriceInt : null,
      event_capacity: format === 'event' && eventIsTicketed ? eventCapacityInt : null,
      event_is_ticketed: format === 'event' ? eventIsTicketed : false,
      scheduled_at: publishSettings.scheduleAt || undefined,
      audience: publishSettings.audience,
      email_subject: publishSettings.emailSubject || undefined,
      share_to_twitter: publishSettings.shareToTwitter || undefined,
      tweet_text: publishSettings.tweetText || undefined,
      url_slug_override: publishSettings.urlSlug || undefined,
      canonical_url: publishSettings.canonicalUrl || undefined,
      comments_enabled: publishSettings.commentsEnabled,
      notes_enabled: publishSettings.notesEnabled,
      comments_subscriber_only: publishSettings.commentsSubscriberOnly,
      cover_alt_text: publishSettings.coverAlt || undefined,
      send_email_notification: false,
    }
    try {
        const preflight = await preflightPublishReadiness(publishPayload)
        const blockingMsg = getReadinessBlockingMessage(preflight)
        if (blockingMsg) {
          setError(blockingMsg)
          showToast('WARN', 'Publish blocked', blockingMsg)
          return
        }
        const warnings = preflight.warnings || []
        if (warnings.length) {
          showToast('INFO', 'Publish warning', warnings.map((w) => w.message).join(' '))
        }
        if (serverDraftId && !fresh) {
          try {
            await updateDraft(serverDraftId, {
              title,
              subtitle,
              format,
              category,
              micro_text: format === 'micro' ? String(microText || '').trim() : microText,
              micro_template_id: microTemplateId,
              cover_image_url: safeCover || '',
              cover_landscape_url: safeCoverLandscape || '',
              body_plain: plain || '.',
              access_level: accessLevel,
              ppv_price_pixe: effectivePpv,
              required_tier_rank: requiredTierRank || null,
              ...buildLocalPayload({
                enabled: isLocalPost,
                mode: localTargetMode,
                distance: localDistance,
                cityLabel: resolvedLocalCity,
                lat: parsedLocalLat,
                lng: parsedLocalLng,
              }),
              event_starts_at: format === 'event' ? (eventStartsAt || null) : null,
              event_ends_at: format === 'event' ? (eventEndsAt || null) : null,
              event_venue_name: format === 'event' ? String(eventVenueName || '').trim() : '',
              event_address: format === 'event' ? String(eventAddress || '').trim() : '',
              event_ticket_price_pixe: format === 'event' && eventIsTicketed ? eventTicketPriceInt : null,
              event_capacity: format === 'event' && eventIsTicketed ? eventCapacityInt : null,
              event_is_ticketed: format === 'event' ? eventIsTicketed : false,
              scheduled_at: publishSettings.scheduleAt || null,
              audience: publishSettings.audience,
              email_subject: publishSettings.emailSubject || '',
              share_to_twitter: publishSettings.shareToTwitter,
              tweet_text: publishSettings.tweetText || '',
              url_slug_override: publishSettings.urlSlug || '',
              canonical_url: publishSettings.canonicalUrl || '',
              comments_enabled: publishSettings.commentsEnabled,
              notes_enabled: publishSettings.notesEnabled,
              comments_subscriber_only: publishSettings.commentsSubscriberOnly,
              cover_alt_text: publishSettings.coverAlt || '',
              send_email_notification: false,
            })
            await patchPostBody(
              serverDraftId,
              bodyPatchPayload
            )
            const published = await publishDraft(serverDraftId, { idempotencyKey: publishAttemptKey })
            try { await deleteDraft(serverDraftId) } catch {}
            markJustPublished()
            if (isPendingReviewPublish(published)) {
              showToast('INFO', 'Under review', 'Submitted for review. It is visible to you while pending.')
            } else if (isQueuedPublish(published)) {
              showToast('INFO', 'Publish queued', 'Chain is unreachable; anchoring will retry and email a receipt when finalized.')
            } else {
              showToast('OK', 'Published', title || 'Your post is live')
            }
            setTimeout(() => {
              resetAfterPublish()
              nav(`/p/${published.id}`)
            }, 150)
          return
        } catch (err: any) {
          if (err?.response?.status !== 404) throw err
          clearServerDraftId()
          // fall through to create a brand new post
        }
      }
        // Always publish through the draft flow so we can attach rich HTML before publishing,
        // and avoid PATCHing /body/ on a non-draft post (which may 404 intentionally).
        const createdDraft = await createDraft({
          title,
          subtitle,
          format,
          category,
          micro_text: format === 'micro' ? String(microText || '').trim() : microText,
          micro_template_id: microTemplateId,
          cover_image_url: safeCover || '',
          cover_landscape_url: safeCoverLandscape || '',
          body_plain: plain || '.',
          access_level: accessLevel,
          ppv_price_pixe: effectivePpv,
          required_tier_rank: requiredTierRank || null,
          ...buildLocalPayload({
            enabled: isLocalPost,
            mode: localTargetMode,
            distance: localDistance,
            cityLabel: resolvedLocalCity,
            lat: parsedLocalLat,
            lng: parsedLocalLng,
          }),
          event_starts_at: format === 'event' ? (eventStartsAt || null) : null,
          event_ends_at: format === 'event' ? (eventEndsAt || null) : null,
          event_venue_name: format === 'event' ? String(eventVenueName || '').trim() : '',
          event_address: format === 'event' ? String(eventAddress || '').trim() : '',
          event_ticket_price_pixe: format === 'event' && eventIsTicketed ? eventTicketPriceInt : null,
          event_capacity: format === 'event' && eventIsTicketed ? eventCapacityInt : null,
          event_is_ticketed: format === 'event' ? eventIsTicketed : false,
          scheduled_at: publishSettings.scheduleAt || null,
          audience: publishSettings.audience,
          email_subject: publishSettings.emailSubject || '',
          share_to_twitter: publishSettings.shareToTwitter,
          tweet_text: publishSettings.tweetText || '',
          url_slug_override: publishSettings.urlSlug || '',
          canonical_url: publishSettings.canonicalUrl || '',
          comments_enabled: publishSettings.commentsEnabled,
          notes_enabled: publishSettings.notesEnabled,
          comments_subscriber_only: publishSettings.commentsSubscriberOnly,
          cover_alt_text: publishSettings.coverAlt || '',
          send_email_notification: false,
        })
        await patchPostBody(
          createdDraft.id,
          bodyPatchPayload
        )
        const published = await publishDraft(createdDraft.id, { idempotencyKey: publishAttemptKey })
        try { await deleteDraft(createdDraft.id) } catch {}
        markJustPublished()
        if (isPendingReviewPublish(published)) {
          showToast('INFO', 'Under review', 'Submitted for review. It is visible to you while pending.')
        } else if (isQueuedPublish(published)) {
          showToast('INFO', 'Publish queued', 'Chain is unreachable; anchoring will retry and email a receipt when finalized.')
        } else {
          showToast('OK', 'Published', title || 'Your post is live')
        }
        setTimeout(() => {
          resetAfterPublish()
          nav(`/p/${published.id}`)
        }, 150)
    } catch (e: any) {
      const networkFee = normalizeInsufficientNetworkFeePayload(e?.response?.data)
      if (e?.response?.status === 402 && networkFee) {
        setError(networkFee.detail)
        showToast(
          'WARN',
          'Network fee required',
          `${networkFee.detail} (on-chain ${networkFee.current_onchain_pixe}/${networkFee.required_onchain_pixe} PIXE)`
        )
        return
      }
      const insufficient = normalizeInsufficientPixePayload(e?.response?.data)
      if (e?.response?.status === 402 && insufficient) {
        setInsufficientPixeGate(insufficient)
        setError(null)
        return
      }
      const msg = e?.response?.data?.detail || e?.message || 'Failed to publish'
      setError(msg)
      showToast('WARN', 'Publish failed', msg)
    } finally {
      setSaving(false)
    }
  }

  // Toolbar actions (TipTap commands via ref)
  function refreshTB() { richRef.current?.refreshToolbarState() }
  function toggleBold() { richRef.current?.toggleBold(); refreshTB() }
  function toggleItalic() { richRef.current?.toggleItalic(); refreshTB() }
  function toggleStrike() { richRef.current?.toggleStrike(); refreshTB() }
  function toggleCode() { richRef.current?.toggleCode(); refreshTB() }
  function toggleCodeBlock() { richRef.current?.toggleCodeBlock(); refreshTB() }
  function insertDivider() { richRef.current?.insertDivider(); refreshTB() }
  function undo() { richRef.current?.undo(); refreshTB() }
  function redo() { richRef.current?.redo(); refreshTB() }
  function clearFormatting() { richRef.current?.clearFormatting(); refreshTB() }
  function toggleUnderline() { richRef.current?.toggleUnderline(); refreshTB() }
  function alignLeft() { richRef.current?.setTextAlign('left'); refreshTB() }
  function alignCenter() { richRef.current?.setTextAlign('center'); refreshTB() }
  function alignRight() { richRef.current?.setTextAlign('right'); refreshTB() }
  function alignJustify() { richRef.current?.setTextAlign('justify'); refreshTB() }
  function setH1() { richRef.current?.setHeading(1); refreshTB() }
  function setH2() { richRef.current?.setHeading(2); refreshTB() }
  function setH3() { richRef.current?.setHeading(3); refreshTB() }
  function setH4() { richRef.current?.setHeading(4); refreshTB() }
  function toggleBullet() { richRef.current?.toggleBulletList() }
  function toggleOrdered() { richRef.current?.toggleOrderedList() }
  function toggleQuote() { richRef.current?.toggleBlockquote() }
  function toggleHighlight() { richRef.current?.toggleHighlight() }

  function addLink() {
    setError(null)
    if (richRef.current?.hasActiveImage()) {
      setLinkComposerMode('image')
      setLinkComposerText('')
      setLinkComposerUrl(richRef.current.getActiveImageLink() || '')
      setLinkComposerOpen(true)
      return
    }
    const selected = String(richRef.current?.getSelectionText() || '').trim()
    setLinkComposerMode('text')
    setLinkComposerText(selected)
    setLinkComposerUrl('')
    setLinkComposerOpen(true)
  }

  function removeLink() {
    if (richRef.current?.hasActiveImage()) {
      richRef.current?.unsetActiveImageLink()
      syncEditorBodyFromRef()
      return
    }
    richRef.current?.unsetLink()
    syncEditorBodyFromRef()
  }

  function applyLinkFromComposer() {
    const url = normalizeExternalUrl(linkComposerUrl)
    if (!url) {
      setError('Enter a valid URL. Example: https://example.com')
      showToast('WARN', 'Invalid link', 'Use a full URL like https://example.com')
      return
    }
    if (linkComposerMode === 'image' && richRef.current?.hasActiveImage()) {
      richRef.current?.setActiveImageLink(url)
    } else {
      const hasSel = !!(richRef.current?.getSelectionText())
      if (hasSel) {
        richRef.current?.setLink(url)
      } else {
        const text = String(linkComposerText || '').trim() || url
        const safeText = escapeHtml(text)
        const safeUrl = escapeHtml(url)
        const frag = `<a href="${safeUrl}" rel="noopener noreferrer nofollow" target="_blank">${safeText}</a>`
        richRef.current?.appendHTML(frag)
      }
    }
    syncEditorBodyFromRef()
    setLinkComposerOpen(false)
    setLinkComposerMode('text')
    setLinkComposerUrl('')
    setLinkComposerText('')
  }

  function addImage() {
    setError(null)
    setUploadError(null)
    editorImageInputRef.current?.click()
  }

  async function handleEditorImageFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const orig = e.target.files?.[0]
    if (!orig) return
    setError(null)
    setUploadError(null)
    setEditorImageUploading(true)
    try {
      const { file } = await prepareImageFile(orig)
      const uploaded = await uploadCoverDirect(file, {
        upload_content_type: 'post_image',
        target_field: 'cover_image_url',
        object_type: 'posts.upload',
      })
      if (String(uploaded?.moderation_status || '').toLowerCase() === 'blocked') {
        throw new Error("This image can't be posted.")
      }
      const publicUrl = String(uploaded?.public_url || '').trim()
      const previewUrl = String(uploaded?.render_url || uploaded?.preview_url || publicUrl || '').trim()
      if (!publicUrl) throw new Error('Upload did not return a URL')
      richRef.current?.insertImage(previewUrl || publicUrl, '')
      syncEditorBodyFromRef()
      showToast('OK', 'Image inserted', 'Reader page image uploaded successfully')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Image upload failed'
      setError(msg)
      setUploadError(msg)
      showToast('WARN', 'Image upload failed', msg)
    } finally {
      setEditorImageUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  // ----- Drafts (local, named) -----
  type DraftItem = {
    id: string
    title: string
    subtitle?: string
    format?: 'micro' | 'zine' | 'book' | 'event' | 'job'
    category?: string
    micro_text?: string
    micro_template_id?: string
    cover?: string
    cover_landscape?: string
    html: string
    access_level?: AccessLevel
    ppv_price_pixe?: string
    required_tier_rank?: string
    is_local?: boolean
    local_city?: string
    local_region?: string
    local_country?: string
    local_lat?: string
    local_lng?: string
    local_distance?: LocalDistanceOption
    local_target_mode?: LocalTargetMode
    event_starts_at?: string
    event_ends_at?: string
    event_venue_name?: string
    event_address?: string
    event_is_ticketed?: boolean
    event_ticket_price_pixe?: string
    event_capacity?: string
    updated_at: string
    created_at: string
  }

  const [toast, setToast] = useState<{ visible: boolean; emoji: string; label: string; detail?: string }>({ visible: false, emoji: 'OK', label: '', detail: undefined })
  const [bodySaveStatus, setBodySaveStatus] = useState<BodySaveStatus>({
    jsonAvailable: false,
    mode: 'html',
    jsonChars: 0,
    jsonNodes: 0,
    source: null,
    updatedAt: null,
  })
  const [autosaveMessage, setAutosaveMessage] = useState<string | null>(null)
  const autosaveRetryRef = useRef<(() => Promise<void>) | null>(null)
  const autosaveRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autosaveRetryDelayRef = useRef<number>(4000)
  const autosaveLastToastAtRef = useRef<number>(0)
  const serverDraftIdRef = useRef<number | null>(null)
  const hideToast = () => setToast((t) => ({ ...t, visible: false }))
  const showToast = (emoji: string, label: string, detail?: string) => {
    setToast({ visible: true, emoji, label, detail })
  }
  const showJsonSaveStatus = Boolean(import.meta.env.DEV || String(import.meta.env.VITE_EDITOR_JSON_STATUS || '').trim() === '1')

  function countJsonNodes(value: any): number {
    if (!value || typeof value !== 'object') return 0
    if (Array.isArray(value)) return value.reduce((sum, item) => sum + countJsonNodes(item), 0)
    const asRecord = value as Record<string, any>
    let count = typeof asRecord.type === 'string' ? 1 : 0
    if (Array.isArray(asRecord.content)) {
      count += asRecord.content.reduce((sum: number, child: any) => sum + countJsonNodes(child), 0)
    }
    return count
  }

  function buildBodyPatchPayload(plainText: string, htmlValue: string, source: BodyPatchSource) {
    const json = richRef.current?.getJSON?.()
    if (json && typeof json === 'object') {
      const jsonString = JSON.stringify(json)
      setBodySaveStatus({
        jsonAvailable: true,
        mode: 'json',
        jsonChars: jsonString.length,
        jsonNodes: countJsonNodes(json),
        source,
        updatedAt: Date.now(),
      })
      return { plain: plainText, json }
    }
    setBodySaveStatus({
      jsonAvailable: false,
      mode: 'html',
      jsonChars: 0,
      jsonNodes: 0,
      source,
      updatedAt: Date.now(),
    })
    if (source === 'publish' && (format === 'zine' || format === 'book')) {
      showToast('WARN', 'Saving via HTML fallback', 'Layout may not persist until editor JSON is available.')
    }
    return { plain: plainText, html: htmlValue }
  }

  function loadDraftsIndex(): Array<{ id: string; title: string; updated_at: string }> {
    try { return JSON.parse(localStorage.getItem('drafts:index') || '[]') } catch { return [] }
  }
  function saveDraftsIndex(list: Array<{ id: string; title: string; updated_at: string }>) {
    localStorage.setItem('drafts:index', JSON.stringify(list))
  }
  function loadDraft(id: string): DraftItem | null {
    try { const raw = localStorage.getItem(`drafts:item:${id}`); return raw ? JSON.parse(raw) : null } catch { return null }
  }
  function saveDraftItem(item: DraftItem) {
    localStorage.setItem(`drafts:item:${item.id}`, JSON.stringify(item))
  }

  function sanitizeCoverUrl(url?: string | null): string {
    if (!url) return ''
    const trimmed = String(url).trim()
    return /^https?:\/\//i.test(trimmed) ? trimmed : ''
  }

  function normalizeExternalUrl(input?: string | null): string {
    const trimmed = String(input || '').trim()
    if (!trimmed) return ''
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmed)) return `https://${trimmed}`
    return ''
  }

  function syncEditorBodyFromRef() {
    const nextHtml = richRef.current?.getHTML() || ''
    setHtml(nextHtml)
    setBody(editorTextFromHtml(nextHtml))
    setDirty(true)
  }

  function createPublishAttemptKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `publish-${crypto.randomUUID()}`
    }
    return `publish-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  useEffect(() => {
    serverDraftIdRef.current = serverDraftId
  }, [serverDraftId])

  function clearAutosaveFailure() {
    setAutosaveMessage(null)
    autosaveRetryRef.current = null
    autosaveRetryDelayRef.current = 4000
    if (autosaveRetryTimerRef.current) {
      clearTimeout(autosaveRetryTimerRef.current)
      autosaveRetryTimerRef.current = null
    }
  }

  function scheduleAutosaveRetry() {
    if (autosaveRetryTimerRef.current) clearTimeout(autosaveRetryTimerRef.current)
    autosaveRetryTimerRef.current = setTimeout(() => {
      if (autosaveRetryRef.current) void autosaveRetryRef.current()
    }, autosaveRetryDelayRef.current)
    autosaveRetryDelayRef.current = Math.min(30000, autosaveRetryDelayRef.current * 2)
  }

  function reportAutosaveFailure(message: string, retryOp: () => Promise<void>, err?: unknown) {
    console.error('draft autosave failed', err)
    autosaveRetryRef.current = retryOp
    setAutosaveMessage(message)
    const now = Date.now()
    if (now - autosaveLastToastAtRef.current > 30000) {
      autosaveLastToastAtRef.current = now
      showToast('WARN', 'Autosave failed', 'Retrying in background')
    }
    scheduleAutosaveRetry()
  }

  async function runDraftSync(op: () => Promise<void>) {
    try {
      await op()
      clearAutosaveFailure()
    } catch (err) {
      reportAutosaveFailure('Autosave failed. Retrying...', () => runDraftSync(op), err)
    }
  }

  function onSaveDraft() {
    const now = new Date().toISOString()
    let id = currentDraftId
    if (!id) id = `d-${Date.now()}`
    const currentHtml = richRef.current?.getHTML() || html || ''
    const safeCover = sanitizeCoverUrl(coverUrl)
    const safeCoverLandscape = sanitizeCoverUrl(coverLandscapeUrl)
    const item: DraftItem = {
      id,
      title,
      subtitle,
      format,
      category,
      micro_text: microText,
      micro_template_id: microTemplateId,
      cover: safeCover,
      cover_landscape: safeCoverLandscape,
      html: currentHtml,
      access_level: accessLevel,
      ppv_price_pixe: ppvPrice,
      required_tier_rank: requiredTierRank ? String(requiredTierRank) : '',
      is_local: isLocalPost,
      local_city: localCity,
      local_lat: localLat,
      local_lng: localLng,
      local_distance: localDistance,
      local_target_mode: localTargetMode,
      event_starts_at: eventStartsAt,
      event_ends_at: eventEndsAt,
      event_venue_name: eventVenueName,
      event_address: eventAddress,
      event_is_ticketed: eventIsTicketed,
      event_ticket_price_pixe: eventTicketPricePixe,
      event_capacity: eventCapacity,
      created_at: now,
      updated_at: now,
    }
    saveDraftItem(item)
    const idx = loadDraftsIndex()
    const existing = idx.find((d) => d.id === id)
    if (existing) { existing.title = title || existing.title; existing.updated_at = now } else { idx.unshift({ id, title: title || 'Untitled', updated_at: now }) }
    saveDraftsIndex(idx)
    setDraftsList(idx)
    setCurrentDraftId(id)
    localStorage.setItem('drafts:current_id', id)
    // Internal toast state: keep values ASCII-safe (avoid encoding artifacts).
    setToast({ visible: true, emoji: 'INFO', label: 'Draft saved', detail: title || 'Untitled' })
    showToast('INFO', 'Draft saved', title || 'Untitled')
    if (authed) {
      const plain = editorTextFromHtml(item.html)
      const syncDraftToServer = async () => {
        const effectivePpv = accessRequiresPPV(accessLevel) ? (ppvInt || null) : null
        const bodyPatchPayload = buildBodyPatchPayload(plain, item.html, 'save_draft')
        const payload = {
          title,
          subtitle,
          format,
          category,
          micro_text: microText,
          micro_template_id: microTemplateId,
          cover_image_url: safeCover || '',
          cover_landscape_url: safeCoverLandscape || '',
          body_plain: plain || '.',
          access_level: accessLevel,
          ppv_price_pixe: effectivePpv,
          required_tier_rank: requiredTierRank || null,
          ...buildLocalPayload({
            enabled: isLocalPost,
            mode: localTargetMode,
            distance: localDistance,
            cityLabel: resolvedLocalCity,
            lat: parsedLocalLat,
            lng: parsedLocalLng,
          }),
          event_starts_at: format === 'event' ? (eventStartsAt || null) : null,
          event_ends_at: format === 'event' ? (eventEndsAt || null) : null,
          event_venue_name: format === 'event' ? String(eventVenueName || '').trim() : '',
          event_address: format === 'event' ? String(eventAddress || '').trim() : '',
          event_ticket_price_pixe: format === 'event' && eventIsTicketed ? eventTicketPriceInt : null,
          event_capacity: format === 'event' && eventIsTicketed ? eventCapacityInt : null,
          event_is_ticketed: format === 'event' ? eventIsTicketed : false,
          scheduled_at: publishSettings.scheduleAt || null,
          audience: publishSettings.audience,
          email_subject: publishSettings.emailSubject || '',
          share_to_twitter: publishSettings.shareToTwitter,
          tweet_text: publishSettings.tweetText || '',
          url_slug_override: publishSettings.urlSlug || '',
          canonical_url: publishSettings.canonicalUrl || '',
          comments_enabled: publishSettings.commentsEnabled,
          notes_enabled: publishSettings.notesEnabled,
          comments_subscriber_only: publishSettings.commentsSubscriberOnly,
          cover_alt_text: publishSettings.coverAlt || '',
          send_email_notification: false,
        }
        const activeServerDraftId = serverDraftIdRef.current
        if (activeServerDraftId) {
          await updateDraft(activeServerDraftId, payload)
          await patchPostBody(activeServerDraftId, bodyPatchPayload)
          return
        }
        const created = await createDraft(payload)
        setServerDraftId(created.id)
        localStorage.setItem('drafts:server_id', String(created.id))
        await patchPostBody(created.id, bodyPatchPayload)
      }
      void runDraftSync(syncDraftToServer)
    }
  }

  function onSelectDraft(id: string) {
    if (!id) return
    const d = loadDraft(id)
    if (!d) return
    setCurrentDraftId(id)
    localStorage.setItem('drafts:current_id', id)
    setTitle(d.title || '')
    setSubtitle(d.subtitle || '')
    setFormat((d.format as any) || 'micro')
    setCategory((d.category as any) || '')
    setMicroText((d.micro_text as any) || '')
    setMicroTemplateId((d.micro_template_id as any) || MICRO_TEMPLATES[0].id)
    setCoverUrl(d.cover || '')
    setCoverLandscapeUrl(d.cover_landscape || '')
    setCoverPreviewUrl(undefined)
    setCoverLandscapePreviewUrl(undefined)
    setHtml(d.html || '')
    setBody(editorTextFromHtml(d.html || ''))
    setAccessLevel((d.access_level as AccessLevel) || 'FREE')
    setPpvPrice((d.ppv_price_pixe as string) || '')
    setRequiredTierRank(d.required_tier_rank ? Number(d.required_tier_rank) : null)
    setIsLocalPost(Boolean(d.is_local))
    setLocalCity(String(d.local_city || ''))
    setLocalLat(String(d.local_lat || ''))
    setLocalLng(String(d.local_lng || ''))
    setLocalDistance((d.local_distance as LocalDistanceOption) || 25)
    const draftLocalMode = String(d.local_target_mode || '').trim()
    if (draftLocalMode === 'near_me' || draftLocalMode === 'profile_city' || draftLocalMode === 'custom_city') {
      setLocalTargetMode(draftLocalMode as LocalTargetMode)
    } else if (draftLocalMode === 'my_city') {
      setLocalTargetMode('profile_city')
    } else if (draftLocalMode === 'custom') {
      setLocalTargetMode('custom_city')
    } else {
      setLocalTargetMode('profile_city')
    }
    setEventStartsAt(String(d.event_starts_at || ''))
    setEventEndsAt(String(d.event_ends_at || ''))
    setEventVenueName(String(d.event_venue_name || ''))
    setEventAddress(String(d.event_address || ''))
    setEventIsTicketed(Boolean(d.event_is_ticketed))
    setEventTicketPricePixe(String(d.event_ticket_price_pixe || ''))
    setEventCapacity(String(d.event_capacity || ''))
    if (richRef.current) richRef.current.setHTML(d.html || '')
  }

  function onDeleteDraft() {
    if (!currentDraftId) return
    localStorage.removeItem(`drafts:item:${currentDraftId}`)
    const next = loadDraftsIndex().filter((d) => d.id !== currentDraftId)
    saveDraftsIndex(next)
    setDraftsList(next)
    setCurrentDraftId(null)
    localStorage.removeItem('drafts:current_id')
    clearServerDraftId()
    clearLocalDraftStorage()
    resetEditorToBlank()
    if (serverDraftId) {
      deleteDraft(serverDraftId).catch((err) => {
        console.error('failed to delete server draft during local delete', err)
      })
    }
  }
  function onCancelPublish() {
    if (currentDraftId) removeLocalDraftRecord(currentDraftId)
    clearLocalDraftStorage()
    clearServerDraftId()
    resetEditorToBlank()
    if (serverDraftId) {
      deleteDraft(serverDraftId).catch((err) => {
        console.error('failed to delete server draft during publish cancel', err)
      })
    }
    showToast('OK', 'Draft cleared')
    nav('/')
  }


  // Deprecated (replaced by chat below). Kept for compatibility; does nothing.
  async function onAIGenerate() { return }

  async function onChatSend() {
    if (!authed) { nav('/login'); return }
    const input = chatInput.trim()
    if (!input) return
    setChatInput('')
    setError(null)
    setMessages((m) => [...m, { role: 'user', content: input }])
    try {
      // Detect formatting intent
      const wantsFormatting = /(\bformat\b|rich\s*text|\bstyle\b|bold|italic|underline|headings?|\bh1\b|\bh2\b|align|justify)/i.test(input)
      // Mode-specific pinned instruction
      const pinned = aiMode === 'brainstorm'
        ? 'You are a brainstorm partner. Respond in plain text with short bullet ideas. Ask one clarifying question when helpful.'
        : aiMode === 'editor'
        ? 'You are an editor. Edit only the selected passage if provided. Keep the author\'s voice. Return only an HTML fragment using <p>, <strong>, <em>, <h2>, <h3>, <ul>, <ol>, <li>, <blockquote>, <a>, <mark>, <hr>. No Markdown, no code fences, no extra commentary.'
        : 'You are a narrative partner. First-person, warm, reflective. Continue the story from the latest context in one short paragraph. Plain text only.'

      // Focused context
      const selection = richRef.current?.getSelectionText() || ''
      const bodyPlain = editorTextFromHtml(html)
      let focus = selection.trim() ? selection : (aiMode === 'story' ? bodyPlain.slice(-800) : '')

      // Editor mode: if no selection and user clearly asked to format, apply to full document
      if (aiMode === 'editor' && !selection.trim() && wantsFormatting) {
        focus = bodyPlain
      } else if (aiMode === 'editor' && !selection.trim()) {
        setError('Select text to edit, or switch to Brainstorm/Story mode.')
        return
      }

      // If not in editor mode but user asked to format, guide them to switch
      if (aiMode !== 'editor' && wantsFormatting) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              'Tip: Switch to Editor mode to format text. Select a section to edit, or say "format entire article" to apply changes to the whole document.',
          },
        ])
        return
      }

      // Compose messages payload with recent history (keeps continuity)
      const msgs: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = []
      if (aiSummary.trim()) msgs.push({ role: 'system', content: `Conversation summary: ${aiSummary.trim()}` })
      msgs.push({ role: 'system', content: pinned })
      const articleContext = editorTextFromHtml(html).slice(0, 2000)
      if (articleContext) {
        msgs.push({ role: 'system', content: `Current article context (plain text):\n${articleContext}` })
      }
      // include last few chat turns to retain context
      const recent = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }))
      if (recent.length) msgs.push(...recent)
      // add focused context for this turn
      if (focus) msgs.push({ role: 'system', content: `Context:\n${focus}` })
      // If the user asked to format, add a stronger formatting directive
      if (aiMode === 'editor' && wantsFormatting) {
        msgs.push({ role: 'system', content: 'Formatting instruction: produce polished magazine-style structure using only allowed HTML tags; prefer <h2>/<h3> for sections, <p> for paragraphs, <strong>/<em> for emphasis, and lists where appropriate. No Markdown.' })
      }
      // current user input
      msgs.push({ role: 'user', content: input })

      const res = await ai.run({ messages: msgs, summary: aiSummary || undefined })
      const text = (res as any)?.content || (res as any)?.body || (res as any)?.text || ''

      // Update summary from server metadata when present
      const meta = (res as any)?.metadata || {}
      if (meta && meta.auto_summary) setAiSummary(String(meta.auto_summary))

      // Apply depending on mode
      if (aiMode === 'editor') {
        applyAssistantReplace(text)
      } else if (aiMode === 'story' && autoAppendStory) {
        applyAssistantAppend(text)
      }

      setMessages((m) => [...m, { role: 'assistant', content: text }])
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'AI request failed'
      setError(detail)
    }
  }

  function applyAssistantReplace(text: string) {
    const fragment = ensureHtmlFromAI(text)
    const sel = richRef.current?.getSelectionText() || ''
    if (sel) {
      richRef.current?.replaceSelectionWithHTML(fragment)
      const h = richRef.current?.getHTML() || ''
      setHtml(h); setBody(editorTextFromHtml(h))
    } else {
      setHtml(fragment)
      setBody(editorTextFromHtml(fragment))
      richRef.current?.setHTML(fragment)
    }
  }

  function applyAssistantAppend(text: string) {
    const fragment = ensureHtmlFromAI(text)
    richRef.current?.appendHTML(fragment)
    const h = richRef.current?.getHTML() || ''
    setHtml(h); setBody(editorTextFromHtml(h))
  }

  function firstUrlIn(s: string): string | null {
    const m = s.match(/https?:\/\/[\w\-._~:?#\[\]@!$&'()*+,;=%/]+/)
    return m ? m[0] : null
  }

  function applyAssistantImage(text: string) {
    const url = firstUrlIn(text)
    if (url) setCoverUrl(url)
  }

  // --- Cover AI helpers ---
  function editorRefShim(): string {
    return editorTextFromHtml(html)
  }

function buildCoverPrompt(): string {
  const plain = normalizeWhitespace(editorRefShim()).slice(0, 800)
  const baseTitle = normalizeWhitespace(title || 'Untitled story')
  const subtitleSnippet = normalizeWhitespace(subtitle || '')
  const descriptor = subtitleSnippet ? `${baseTitle} - ${subtitleSnippet}` : baseTitle
  const context = plain ? `Story context: ${plain}.` : 'Story context: author is drafting a new post.'
  const keywords = Array.from(new Set(tokenizeWords(plain).filter((token) => token.length > 3))).slice(0, 12)
  const motifs = keywords.length ? `Key motifs: ${keywords.join(', ')}.` : ''
  return `Create editorial-quality cover art (no text or lettering, avoid typography overlays) for "${descriptor}". ${context} ${motifs} Focus on the tone implied by the article and avoid literal magazine mastheads or body copy.`
    .replace(/\s+/g, ' ')
    .trim()
}

  function resolveGeneratedImagePayload(img: any): { preview: string; publicUrl: string | null; dataUrl: string | null; remoteUrl: string | null } {
    if (!img) throw new Error('No image returned')
    const base64 = typeof img?.b64_json === 'string' && img.b64_json ? `data:image/png;base64,${img.b64_json}` : null
    const hosted = typeof img?.public_url === 'string' && img.public_url ? img.public_url : null
    const remoteUrl = typeof img?.url === 'string' && img.url ? img.url : null
    if (hosted) {
      return { preview: hosted, publicUrl: hosted, dataUrl: base64, remoteUrl }
    }
    if (remoteUrl) {
      return { preview: remoteUrl, publicUrl: null, dataUrl: base64, remoteUrl }
    }
    if (base64) {
      return { preview: base64, publicUrl: null, dataUrl: base64, remoteUrl: null }
    }
    throw new Error('Unsupported image payload')
  }

  const canGenerateCover = Boolean((title && title.trim().length >= 4) || wordCount > 40)

  async function onGenerateCoverAI(customPrompt?: string) {
    const targetPrompt = (customPrompt || coverAiPrompt || '').trim()
    if (!targetPrompt) return
    setCoverAiError(null)
    setCoverStatus("Creating Pixe image...")
    setLastCoverPrompt(targetPrompt)
    setCoverAiLoading(true)
    setCoverAiPreview(null)
    try {
      const res = await generateCoverImage({ prompt: targetPrompt, size: '1024x1024', quality: 'standard' })
      const img = (res.images || [])[0]
      if (!img) throw new Error('No image returned')
      const { preview, publicUrl, dataUrl, remoteUrl } = resolveGeneratedImagePayload(img)
      setCoverAiPreview(preview)
      await onUseGeneratedCover(preview, {
        publicUrl,
        dataUrl,
        remoteUrl,
      })
      setCoverStatus('Cover image created')
    } catch (e: any) {
      setCoverAiError(e?.response?.data?.detail || e?.message || 'Image generation failed')
      setCoverStatus('Cover image failed. Try again.')
    } finally {
      setTimeout(() => setCoverStatus(null), 4000)
      setCoverAiLoading(false)
    }
  }

  async function onUseGeneratedCover(
    previewOverride?: string | null,
    opts?: { publicUrl?: string | null; dataUrl?: string | null; remoteUrl?: string | null },
  ) {
    const preview = typeof previewOverride === 'string' ? previewOverride : coverAiPreview
    if (!preview) return
    try {
      setCoverUrl(preview) // show immediate preview
      setCoverPreviewUrl(preview)
      let publicUrl = preview
      let previewUrl = preview
      let file: File | null = null

      const candidateDataUrl = opts?.dataUrl || (preview.startsWith('data:') ? preview : null)
      if (candidateDataUrl) {
        file = await dataUrlToFile(candidateDataUrl, 'cover-ai.png')
      } else {
        const candidateRemote = opts?.remoteUrl || preview
        file = await urlToFile(candidateRemote, 'cover-ai.png')
      }

      const shouldAutoGenerateLandscape = publishSettings.autoCropLandscape && !coverLandscapeUrl
      if (file) {
        const direct = await uploadCoverDirect(file, {
          upload_content_type: 'post_image',
          target_field: 'cover_image_url',
          object_type: 'posts.upload',
        })
        if (String(direct?.moderation_status || '').toLowerCase() === 'blocked') {
          throw new Error("This image can't be posted.")
        }
        if (String(direct?.moderation_status || '').toLowerCase() === 'held') {
          setCoverModerationState('held')
          setCoverModerationMessage('Submitted for review. You can keep drafting, but this cannot publish publicly yet.')
        } else {
          setCoverModerationState('passed')
          setCoverModerationMessage('Image scan passed.')
        }
        publicUrl = direct.public_url
        previewUrl = String(direct?.render_url || direct?.preview_url || publicUrl || '').trim() || publicUrl
        if (shouldAutoGenerateLandscape) {
          await autoCropLandscape(file).catch(() => null)
        }
      } else if (opts?.publicUrl) {
        publicUrl = opts.publicUrl
        previewUrl = opts.publicUrl
        if (shouldAutoGenerateLandscape) {
          await autoCropLandscape(publicUrl).catch(() => null)
        }
      } else if (shouldAutoGenerateLandscape) {
        await autoCropLandscape(publicUrl).catch(() => null)
      }
      setCoverUrl(publicUrl)
      setCoverPreviewUrl(previewUrl || publicUrl)
      setCoverAiPreview(previewUrl || publicUrl)
    } catch (e: any) {
      setCoverAiError(e?.message || 'Failed to save cover')
    }
  }

  async function createCoverFromContext() {
    if (!canGenerateCover || coverAiLoading) return
    const prompt = buildCoverPrompt()
    setCoverAiPrompt(prompt)
    setLastCoverPrompt(prompt)
    setCoverStatus("Creating Pixe image...")
    setCoverAiLoading(true)
    try {
      const res = await generateCoverImage({ prompt, size: '1024x1024', quality: 'standard' })
      const img = (res.images || [])[0]
      if (!img) throw new Error('No image returned')
      const { preview, publicUrl, dataUrl, remoteUrl } = resolveGeneratedImagePayload(img)
      setCoverAiPreview(preview)
      await onUseGeneratedCover(preview, {
        publicUrl,
        dataUrl,
        remoteUrl,
      })
      setCoverStatus('Cover image created')
    } catch (e: any) {
      setCoverAiError(e?.response?.data?.detail || e?.message || 'Image generation failed')
      setCoverStatus('Cover image failed. Try again.')
    } finally {
      setTimeout(() => setCoverStatus(null), 4000)
      setCoverAiLoading(false)
    }
  }

  async function regenerateCover() {
    if (!lastCoverPrompt || coverAiLoading) return
    await onGenerateCoverAI(lastCoverPrompt)
  }

  async function generateAITitle() {
    const plain = editorTextFromHtml(html)
    if (!plain.trim()) {
      setError('Add some content before requesting a title.')
      return
    }
    if (ai.loading) return
    setTitleStatus("Generating title...")
    try {
      const prompt = `You are an award-winning editor. Read the article text below and suggest a single compelling magazine-style title (max 8 words). Avoid quotes and punctuation at the ends. Return ONLY the title string.\n\nArticle:\n${plain}`
      const res = await ai.run({ prompt, body: plain })
      const text = ((res as any)?.content || (res as any)?.body || '').trim()
        .replace(/^[\"']+|[\"']+$/g, '')
      if (text) {
        setTitle(text)
        setTitleStatus('Title updated')
      } else {
        setTitleStatus('No title returned. Try again.')
      }
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI title failed')
      setTitleStatus('Failed to generate title.')
    } finally {
      setTimeout(() => setTitleStatus(null), 4000)
    }
  }

  async function generateAISubtitle() {
    const plain = editorTextFromHtml(html)
    if (!plain.trim()) {
      setError('Add some content before requesting a subtitle.')
      return
    }
    if (ai.loading) return
    setSubtitleStatus("Generating subtitle...")
    try {
      const prompt = `You are an award-winning editor. Read the article text below and suggest a single compelling magazine-style subtitle (max 18 words). It should complement the title and invite the reader in. Return ONLY the subtitle string.\n\nArticle:\n${plain}`
      const res = await ai.run({ prompt, body: plain })
      const text = ((res as any)?.content || (res as any)?.body || '').trim()
        .replace(/^[\"']+|[\"']+$/g, '')
      if (text) {
        setSubtitle(text)
        setSubtitleStatus('Subtitle updated')
      } else {
        setSubtitleStatus('No subtitle returned. Try again.')
      }
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI subtitle failed')
      setSubtitleStatus('Failed to generate subtitle.')
    } finally {
      setTimeout(() => setSubtitleStatus(null), 4000)
    }
  }

  async function runAIMode(label: string, instruction: string) {
    const base = richRef.current?.getSelectionText() || body || ''
    if (!base.trim()) return
    setError(null)
    try {
      const prompt = `${instruction}\n\nSelection (or full body) below:\n\n${base}`
      const res = await ai.run({ prompt, body: body })
      const text = (res as any)?.content || ''
      applyAssistantReplace(text)
      setMessages((m)=>[...m, { role: 'user', content: `${label}` }, { role: 'assistant', content: text }])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  // Append variant: good for actions like "Continue story" so we don't overwrite the doc
  async function runAIModeAppend(label: string, instruction: string) {
    const sel = richRef.current?.getSelectionText() || ''
    // Use selection when present; otherwise use the last ~800 chars of body for local context
    const context = sel.trim() ? sel : (body || '').slice(-800)
    if (!context.trim()) return
    setError(null)
    try {
      const prompt = `${instruction}\n\nContext below (plain text):\n\n${context}`
      const res = await ai.run({ prompt, body: editorTextFromHtml(html) })
      const text = (res as any)?.content || (res as any)?.body || (res as any)?.text || ''
      applyAssistantAppend(text)
      setMessages((m)=>[...m, { role: 'user', content: `${label}` }, { role: 'assistant', content: text }])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  // Format the entire article into a polished magazine-style HTML fragment
  async function runAIFormatWhole() {
    const sourceHTML = richRef.current?.getHTML() || html || ''
    const bodyPlain = editorTextFromHtml(sourceHTML)
    if (!bodyPlain.trim()) return
    setError(null)
    try {
      // Ensure we are in Editor mode for formatting; auto-switch if needed
      if (aiMode !== 'editor') {
        setAiMode('editor')
        setMessages((m)=>[...m, { role: 'assistant', content: 'Switching to Editor mode and applying magazine-style formatting to the whole article.' }])
      }
      const systemMsg = [
        'You are an award-winning long-form editor.',
        'Your job is to reformat the provided article into a polished magazine layout.',
        'You MUST retain every fact, sentence, quote, and bullet exactly as given - no new ideas, no summarizing, no omissions.',
        'Preserve the original casing and punctuation; do NOT add decorative drop caps, extra quotation marks, or shout-case headings.',
        'The output must be a single HTML fragment using only: <p>, <strong>, <em>, <h2>, <h3>, <ul>, <ol>, <li>, <blockquote>, <a>, <mark>, <hr>.',
        "Ensure the opening paragraph begins with a bold lead-in (<strong>...</strong>), include at least one <blockquote> sourced from the text, and keep any lists/bullets by rewriting them inside <ul><li>...</li></ul> with the same wording.",
        'Never wrap with <html> or <body>; no Markdown.'
      ].join(' ')
      const userMsg = [
        'Reformat the article below. Improve flow/sectioning (4-6 sections, <h2> headings, optional <h3>), add <hr> between major sections when it helps readability, and italicize short asides with <em> when natural.',
        'Before responding, double-check that every original sentence still appears (with only light stylistic edits). If anything is missing, reinsert it.',
        '',
        '---ORIGINAL_HTML---',
        sourceHTML,
        '---END_ORIGINAL_HTML---',
        '',
        '---PLAIN_TEXT_REFERENCE---',
        bodyPlain,
        '---END_PLAIN_TEXT_REFERENCE---',
      ].join('\n')
      const res = await ai.run({
        messages: [
          { role: 'system', content: systemMsg },
          { role: 'user', content: userMsg },
        ],
        body: bodyPlain,
      })
      const text = ((res as any)?.content || (res as any)?.body || (res as any)?.text || '').trim()
      if (!text) {
        setError('Magazine styling returned no content. Article left unchanged.')
        if (sourceHTML) {
          richRef.current?.setHTML(sourceHTML)
          setHtml(sourceHTML)
          setBody(editorTextFromHtml(sourceHTML))
        }
        return
      }
      const newPlain = normalizeWhitespace(editorTextFromHtml(text))
      const oldPlain = normalizeWhitespace(bodyPlain)
      const lengthRatio = oldPlain.length ? newPlain.length / oldPlain.length : 1

      const tailTokens = tokenizeWords(bodyPlain).slice(-80)
      const newTokensSet = new Set(tokenizeWords(newPlain))
      const tailCoverage = tailTokens.length
        ? tailTokens.filter((token) => newTokensSet.has(token)).length / tailTokens.length
        : 1
      const preservesTail = tailCoverage >= 0.25
      const lengthOK = !oldPlain || lengthRatio >= 0.7

      if (!newPlain || !lengthOK || !preservesTail) {
        setError(`Magazine styling appeared to drop some content, so we left your article unchanged (len=${lengthRatio.toFixed(2)}, tail=${(tailCoverage*100).toFixed(0)}%).`)
        if (sourceHTML) {
          richRef.current?.setHTML(sourceHTML)
          setHtml(sourceHTML)
          setBody(editorTextFromHtml(sourceHTML))
        }
        return
      }
      applyAssistantReplace(text) // replaces whole doc when no selection
      setMessages((m)=>[...m, { role: 'user', content: 'Magazine style (format whole article)' }, { role: 'assistant', content: text }])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  const jobPanel = (
    <div className="rounded-xl border border-white/10 p-3 space-y-3">
      <div className="text-sm font-semibold">Job details</div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-xs text-white/70">
          Job kind
          <select value={jobKind} onChange={(e) => {
            const next = (e.target.value as 'CONTRACT' | 'EMPLOYMENT')
            setJobKind(next)
            if (next === 'EMPLOYMENT') setJobVisibility('OPEN')
          }} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm">
            <option value="CONTRACT">Contract</option>
            <option value="EMPLOYMENT">Employment</option>
          </select>
        </label>
        <label className="text-xs text-white/70">
          Visibility
          <select value={jobKind === 'EMPLOYMENT' ? 'OPEN' : jobVisibility} onChange={(e) => setJobVisibility(e.target.value as 'OPEN' | 'DIRECT')} disabled={jobKind === 'EMPLOYMENT'} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm disabled:opacity-60">
            <option value="OPEN">Open</option>
            <option value="DIRECT">Direct</option>
          </select>
        </label>
      </div>
      {jobKind === 'CONTRACT' && jobVisibility === 'DIRECT' ? (
        <label className="text-xs text-white/70 block">
          Target Pro user id
          <input value={jobTargetProUser} onChange={(e) => setJobTargetProUser(e.target.value)} placeholder="e.g. 123" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
      ) : null}
      <label className="text-xs text-white/70 block">
        Summary
        <textarea value={jobSummary} onChange={(e) => setJobSummary(e.target.value)} rows={3} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
      </label>
      <label className="text-xs text-white/70 block">
        Details (markdown)
        <textarea value={jobDetails} onChange={(e) => setJobDetails(e.target.value)} rows={5} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
      </label>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-xs text-white/70">
          Location mode
          <select value={jobLocationMode} onChange={(e) => setJobLocationMode(e.target.value as 'REMOTE' | 'ONSITE' | 'HYBRID')} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm">
            <option value="REMOTE">Remote</option>
            <option value="ONSITE">Onsite</option>
            <option value="HYBRID">Hybrid</option>
          </select>
        </label>
        <label className="text-xs text-white/70">
          Location text
          <input value={jobLocationText} onChange={(e) => setJobLocationText(e.target.value)} placeholder="Remote / San Diego, CA" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <label className="text-xs text-white/70">
          Compensation mode
          <select value={jobCompensationMode} onChange={(e) => setJobCompensationMode(e.target.value as 'FIXED' | 'HOURLY' | 'SALARY')} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm">
            {jobKind === 'CONTRACT' ? <option value="FIXED">Fixed</option> : null}
            <option value="HOURLY">Hourly</option>
            {jobKind === 'EMPLOYMENT' ? <option value="SALARY">Salary</option> : null}
          </select>
        </label>
        <label className="text-xs text-white/70">
          Min rate (PIXE/hr)
          <input value={jobMinRate} onChange={(e) => setJobMinRate(e.target.value)} placeholder="3000" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
        <label className="text-xs text-white/70">
          Max rate (PIXE/hr)
          <input value={jobMaxRate} onChange={(e) => setJobMaxRate(e.target.value)} placeholder="8000" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
      </div>
      {jobCompensationMode === 'HOURLY' ? (
        <div className="text-[11px] text-white/60">Min rate (PIXE/hr), Max rate (PIXE/hr). 1 PIXE = $1 (for now).</div>
      ) : null}
      {jobCompensationMode === 'FIXED' ? (
        <label className="text-xs text-white/70 block">
          Fixed price (PIXE x100)
          <input value={jobFixedPrice} onChange={(e) => setJobFixedPrice(e.target.value)} placeholder="500000" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
      ) : null}
      {jobCompensationMode === 'SALARY' ? (
        <label className="text-xs text-white/70 block">
          Salary (PIXE x100)
          <input value={jobSalary} onChange={(e) => setJobSalary(e.target.value)} placeholder="12000000" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        </label>
      ) : null}
      <label className="text-xs text-white/70 block">
        Skills (comma separated)
        <input value={jobSkills} onChange={(e) => setJobSkills(e.target.value)} placeholder="photography, editing, events" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
      </label>
    </div>
  )

  const localEventPanel = (
    <div className="space-y-2">
      <LocalPublishOptions
        enabled={isLocalPost || format === 'event'}
        onEnabledChange={(next) => setIsLocalPost(format === 'event' ? true : next)}
        distance={localDistance}
        onDistanceChange={setLocalDistance}
        targetMode={localTargetMode}
        onTargetModeChange={setLocalTargetMode}
        hasCoordinates={localHasPoint}
        onUseLocation={onUseMyLocation}
        cityLabel={localCity}
        onCityLabelChange={(next) => {
          setLocalCity(next)
          if (focusCustomCityInput) setFocusCustomCityInput(false)
        }}
        profileCityLabel={profileCityLabel}
        previewLocationLabel={resolvedLocalCity || profileCityLabel}
        forceFocusCityInput={focusCustomCityInput}
      />
      {localMissing ? <div className="text-xs text-red-300">Set location details for Local visibility.</div> : null}

      {format === 'event' ? (
        <div className="rounded-xl border border-white/10 p-3">
          <div className="text-sm font-semibold">Event details</div>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            <EventDateTimePicker
              label="Start"
              required
              value={eventStartsAt || null}
              onChange={(nextIso) => {
                setEventStartsAt(nextIso || '')
                if (!eventEndsAt && nextIso) {
                  const ms = Date.parse(nextIso)
                  if (Number.isFinite(ms)) {
                    setEventEndsAt(new Date(ms + 2 * 60 * 60 * 1000).toISOString())
                  }
                }
              }}
              placeholder="Select start..."
            />
            <EventDateTimePicker
              label="End"
              value={eventEndsAt || null}
              min={eventStartsAt || null}
              onChange={(nextIso) => setEventEndsAt(nextIso || '')}
              placeholder="Select end..."
            />
            <input
              value={eventVenueName}
              onChange={(e) => setEventVenueName(e.target.value)}
              placeholder="Venue name (optional)"
              className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
            />
            <input
              value={eventAddress}
              onChange={(e) => setEventAddress(e.target.value)}
              placeholder="Address (optional)"
              className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
            />
            <label className="inline-flex items-center gap-2 text-sm text-white/85">
              <input type="checkbox" checked={eventIsTicketed} onChange={(e) => setEventIsTicketed(Boolean(e.target.checked))} />
              <span>Ticketed event</span>
            </label>
            {eventIsTicketed ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={eventTicketPricePixe}
                  onChange={(e) => setEventTicketPricePixe(e.target.value)}
                  placeholder="Ticket price (PIXE)"
                  className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                />
                <input
                  value={eventCapacity}
                  onChange={(e) => setEventCapacity(e.target.value)}
                  placeholder="Capacity"
                  className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                />
              </div>
            ) : null}
          </div>
          {eventStartsMissing ? <div className="mt-2 text-xs text-red-300">Start date/time is required for Event.</div> : null}
          {eventEndBeforeStart ? <div className="mt-1 text-xs text-red-300">End date/time cannot be before start.</div> : null}
          {eventLocationMissing ? <div className="mt-1 text-xs text-red-300">Events require a city label or your location.</div> : null}
          {eventTicketFieldsInvalid ? <div className="mt-1 text-xs text-red-300">Ticketed events require price and capacity (1+).</div> : null}
        </div>
      ) : null}
    </div>
  )

  const bodySaveStatusTone: 'ok' | 'warn' | 'info' =
    bodySaveStatus.mode === 'json' ? 'ok' : bodySaveStatus.source ? 'warn' : 'info'
  const bodySaveStatusText = showJsonSaveStatus
    ? bodySaveStatus.source
      ? bodySaveStatus.mode === 'json'
        ? `JSON: yes | Saving via JSON (${bodySaveStatus.source}, nodes: ${bodySaveStatus.jsonNodes}, chars: ${bodySaveStatus.jsonChars})`
        : `JSON: no | Saving via HTML fallback (${bodySaveStatus.source})`
      : 'JSON: pending'
    : null

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />

      <main className="mx-auto max-w-7xl px-4 py-6">
        {toast.visible && (
          <div className="fixed bottom-6 right-6 z-50" role="status" aria-live="polite">
            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-neutral-900/90 px-4 py-3 shadow-2xl">
              <span className="text-lg">{toast.emoji}</span>
              <div className="text-sm">
                <div className="font-semibold text-white">{toast.label}</div>
                {toast.detail && <div className="text-white/70">{toast.detail}</div>}
              </div>
              <button onClick={hideToast} className="text-xs text-white/60 hover:text-white">Dismiss</button>
            </div>
          </div>
        )}
        {!authed && (
          <div className="mb-4 text-sm text-amber-300">
            Please <Link className="underline" to="/login">log in</Link> to publish.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Editor */}
          <section className="lg:col-span-7 space-y-4">
            {format === 'micro' ? (
              <>
                <div className="lg:hidden">
                  <MicroPreviewCard
                    text={microText}
                    templateId={microTemplateId}
                    categoryLabel={microCategoryName || null}
                    authorName={me?.profile?.display_name || me?.profile?.handle || me?.username || null}
                    authorHandle={me?.profile?.handle || me?.username || null}
                    authorAvatarUrl={me?.profile?.avatar_url || null}
                    createdAt={new Date().toISOString()}
                    viewsCount={0}
                    hasProof={false}
                  />
                </div>
                <TitleFields
                  title={title}
                  subtitle={subtitle}
                  onChangeTitle={setTitle}
                  onChangeSubtitle={setSubtitle}
                  hideTitleSubtitle
                  format={format}
                  onChangeFormat={setFormat}
                  category={category}
                  onChangeCategory={setCategory}
                  categories={categories || []}
                />
                {localEventPanel}
                <MicroTextComposer value={microText} onChange={onChangeMicroText} required />
                <MicroTemplatePicker value={microTemplateId} onChange={setMicroTemplateId} templates={MICRO_TEMPLATES} />

                <DraftControls
                  onSaveDraft={onSaveDraft}
                  currentDraftId={currentDraftId}
                  draftsList={draftsList}
                  onSelectDraft={onSelectDraft}
                  onDeleteDraft={onDeleteDraft}
                  statusText={bodySaveStatusText}
                  statusTone={bodySaveStatusTone}
                />

                <details
                  className="rounded-2xl border border-white/10 bg-white/5 p-4"
                  open={microAdvancedOpen}
                  onToggle={(e) => setMicroAdvancedOpen((e.target as HTMLDetailsElement).open)}
                >
                  <summary className="cursor-pointer select-none text-sm font-semibold text-white/85">
                    Reader page
                    <span className="ml-2 text-xs font-normal text-white/60">Shown when someone opens your Micro. Edits reader content only.</span>
                  </summary>

                  <div className="mt-4 space-y-4">
                    {/* Access */}
                    <div className="rounded-xl border border-white/10 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">Access</div>
                          <div className="mt-1 text-xs text-white/60">Choose who can read this Pixe.</div>
                        </div>
                        <div className="text-xs text-white/70">
                          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1">
                            {accessLabel(accessLevel, accessNeedsPrice ? ppvInt : null, requiredTierRank)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {(
                          [
                            { key: 'FREE', label: 'Free' },
                            { key: 'SUBSCRIBERS_ONLY', label: 'Subscribers' },
                            { key: 'PAY_PER_VIEW', label: 'PPV' },
                            { key: 'SUBSCRIBERS_OR_PPV', label: 'Subs or PPV' },
                          ] as Array<{ key: AccessLevel; label: string }>
                        ).map((opt) => {
                          const disabled = isAccessOptionDisabled({ option: opt.key, subsEnabled, ppvAllowed })
                          const active = accessLevel === opt.key
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              disabled={disabled}
                              onClick={() => onPickAccess(opt.key)}
                              className={[
                                'px-3 py-2 rounded-lg border text-sm font-semibold transition',
                                active ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10',
                                disabled ? 'opacity-50 cursor-not-allowed hover:bg-white/5' : '',
                              ].join(' ')}
                            >
                              {opt.label}
                            </button>
                          )
                        })}
                      </div>

                      {!subsEnabled ? (
                        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                          Enable subscriptions to use Subscriber-only or PPV access.{' '}
                          <Link className="underline" to="/settings/creator/subscriptions">
                            Open settings
                          </Link>
                        </div>
                      ) : subsEnabled && !ppvAllowed ? (
                        <div className="mt-3 text-xs text-white/60">
                          Pay-per-view is off. Turn it on in{' '}
                          <Link className="underline" to="/settings/creator/subscriptions">
                            Subscriptions settings
                          </Link>
                          .
                        </div>
                      ) : null}

                      {subsEnabled && hasTiers && (accessLevel === 'SUBSCRIBERS_ONLY' || accessLevel === 'SUBSCRIBERS_OR_PPV') ? (
                        <div className="mt-3">
                          <div className="text-xs font-semibold text-white/70">Minimum tier</div>
                          <select
                            value={requiredTierRank ? String(requiredTierRank) : ''}
                            onChange={(e) => {
                              const raw = (e.target.value || '').trim()
                              if (!raw) setRequiredTierRank(null)
                              else setRequiredTierRank(Number(raw))
                            }}
                            className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                            style={{ colorScheme: 'dark' } as any}
                          >
                            <option value="">Any subscriber</option>
                            {(creatorTiers || [])
                              .filter((t) => t.is_active)
                              .sort((a, b) => (a.rank || 0) - (b.rank || 0))
                              .map((t) => (
                                <option key={t.id} value={String(t.rank)}>
                                  {t.name} (Tier {t.rank}+)
                                </option>
                              ))}
                          </select>
                        </div>
                      ) : null}

                      {accessNeedsPrice ? (
                        <div className="mt-3">
                          <div className="text-xs font-semibold text-white/70">Unlock price (PIXE)</div>
                          <input
                            value={ppvPrice}
                            onChange={(e) => setPpvPrice(e.target.value)}
                            placeholder={String(defaultPpv > 0 ? defaultPpv : 1)}
                            className={[
                              'mt-1 w-full rounded-lg bg-white/5 border px-3 py-2 text-sm outline-none',
                              accessPriceInvalid ? 'border-red-500/30 focus:border-red-500/40' : 'border-white/10 focus:border-white/20',
                            ].join(' ')}
                            inputMode="numeric"
                          />
                          {accessPriceInvalid ? <div className="mt-1 text-xs text-red-300">Enter a whole number (1+).</div> : null}
                        </div>
                      ) : null}
                    </div>

                    {/* Cover uploader */}
                    <div className="rounded-xl border border-white/10 p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={createCoverFromContext}
                            disabled={!canGenerateCover || coverAiLoading || uploading}
                            className={`inline-flex items-center rounded-lg border border-white/15 px-3 py-1.5 text-sm ${(!canGenerateCover || coverAiLoading || uploading) ? 'opacity-60' : 'hover:bg-white/5'}`}
                          >
                            {coverAiLoading ? "Creating..." : "Create Pixe Image"}
                          </button>
                          <button
                            type="button"
                            onClick={regenerateCover}
                            disabled={!lastCoverPrompt || coverAiLoading || uploading}
                            className={`inline-flex items-center rounded-lg border border-white/15 px-3 py-1.5 text-sm ${(!lastCoverPrompt || coverAiLoading || uploading) ? 'opacity-60' : 'hover:bg-white/5'}`}
                          >
                            Regenerate
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCoverAiOpen(true)
                              if (lastCoverPrompt) setCoverAiPrompt(lastCoverPrompt)
                            }}
                            className="text-xs text-white/70 hover:underline"
                          >
                            Adjust prompt
                          </button>
                          <span className="text-xs text-white/60 max-w-xs">
                            {coverStatus ? coverStatus : (!canGenerateCover ? 'Add more text to enable cover generation.' : '')}
                          </span>
                        </div>
                        <div className="text-xs text-white/60">Upload portrait + landscape below.</div>
                      </div>
                      <div className="mt-2 text-xs text-white/60">Accepted: JPG, PNG, WebP. Recommended {'>='} 1600px on the long edge.</div>
                      {coverModerationMessage ? (
                        <div className={`mt-2 text-xs ${coverModerationState === 'blocked' ? 'text-rose-300' : coverModerationState === 'held' ? 'text-amber-300' : 'text-white/70'}`}>
                          {coverModerationMessage}
                        </div>
                      ) : null}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between h-7 mb-1">
                            <div className="text-xs text-white/60 whitespace-nowrap">Portrait (3:4)</div>
                            <div className="flex items-center gap-2">
                              {coverLandscapeUrl && (
                                <button
                                  type="button"
                                  className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10"
                                  onClick={onGeneratePortrait}
                                >
                                  {coverUrl ? 'Regenerate' : 'Generate'}
                                </button>
                              )}
                              {coverUrl && (
                                <button type="button" onClick={onRemovePortrait} className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10">Remove</button>
                              )}
                              <label className="inline-flex items-center rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/5 cursor-pointer">
                                <input type="file" accept="image/*" className="hidden" onChange={handlePortraitFileChange} />
                                {uploading ? "Scanning image..." : "Upload"}
                              </label>
                            </div>
                          </div>
                          {renderCoverUrl ? (
                            <img src={renderCoverUrl} alt={publishSettings.coverAlt || 'Cover image'} className="w-full h-40 object-cover rounded-lg border border-white/10" />
                          ) : (
                            <div className="w-full h-40 rounded-lg border border-white/10 bg-white/5" />
                          )}
                        </div>
                        <div className="md:col-span-3">
                          <div className="flex items-center justify-between h-7 mb-1">
                            <div className="text-xs text-white/60 whitespace-nowrap">Landscape (16:9)</div>
                            <div className="flex items-center gap-2">
                              {coverUrl && (
                                <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={onGenerateLandscape}>
                                  {coverLandscapeUrl ? 'Regenerate' : 'Generate'}
                                </button>
                              )}
                              {coverLandscapeUrl && (
                                <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={onRemoveLandscape}>Clear</button>
                              )}
                              <label className="inline-flex items-center rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/5 cursor-pointer">
                                <input type="file" accept="image/*" className="hidden" onChange={handleLandscapeFileChange} />
                                {uploading ? "Scanning image..." : "Upload"}
                              </label>
                            </div>
                          </div>
                          {renderCoverLandscapeUrl ? (
                            <img src={renderCoverLandscapeUrl} alt={(publishSettings.coverAlt || 'Cover') + ' (landscape)'} className="w-full h-40 object-cover rounded-lg border border-white/10" />
                          ) : (
                            <div className="w-full h-40 rounded-lg border border-white/10 bg-white/5" />
                          )}
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="text-sm text-white/70">Alt text</label>
                        <input value={publishSettings.coverAlt} onChange={(e)=> setPublishSettings((p)=> ({ ...p, coverAlt: e.target.value }))} className="mt-1 w-full rounded bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" placeholder="Describe the cover for readers and SEO" />
                      </div>
                    </div>

                    {/* Toolbar */}
                    <div className="relative text-sm px-2 pt-2 pb-1 rounded-xl bg-neutral-900/60 border border-white/10 shadow-sm pr-64">
                      {/* Row 1 */}
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Inline styles */}
                        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Bold" aria-label="Bold" onClick={toggleBold} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bold ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiBold /></button>
                          <button title="Italic" aria-label="Italic" onClick={toggleItalic} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.italic ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiItalic /></button>
                          <button title="Strikethrough" aria-label="Strikethrough" onClick={toggleStrike} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.strike ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiStrikethrough2 /></button>
                          <button title="Inline code" aria-label="Inline code" onClick={toggleCode} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.code ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiCode /></button>
                          <button title="Underline" aria-label="Underline" onClick={toggleUnderline} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.underline ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiUnderline /></button>
                        </div>
                        <div className="w-px h-6 bg-white/10" />
                        {/* Headings */}
                        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Heading 1" aria-label="Heading 1" onClick={setH1} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h1 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H1</span></button>
                          <button title="Heading 2" aria-label="Heading 2" onClick={setH2} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h2 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H2</span></button>
                          <button title="Heading 3" aria-label="Heading 3" onClick={setH3} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h3 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H3</span></button>
                          <button title="Heading 4" aria-label="Heading 4" onClick={setH4} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h4 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H4</span></button>
                        </div>
                      </div>

                      {/* Row 2 */}
                      <div className="mt-1 flex flex-nowrap items-center gap-1.5">
                        {/* Blocks */}
                        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Bullet list" aria-label="Bullet list" onClick={toggleBullet} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.bullet ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiList /></button>
                          <button title="Numbered list" aria-label="Numbered list" onClick={toggleOrdered} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.ordered ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiListOrdered /></button>
                          <button title="Quote" aria-label="Quote" onClick={toggleQuote} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.quote ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiDoubleQuotesL /></button>
                          <button title="Highlight" aria-label="Highlight" onClick={toggleHighlight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.highlight ? 'bg-amber-500/30 border-amber-400/40 text-amber-100' : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'}`}><RiMarkPenLine /></button>
                          <button title="Code block" aria-label="Code block" onClick={toggleCodeBlock} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.codeBlock ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><BiCodeBlock /></button>
                          <button title="Divider" aria-label="Divider" onClick={insertDivider} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiMinus /></button>
                        </div>
                        <div className="w-px h-6 bg-white/10" />
                        {/* Alignment */}
                        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Align left" aria-label="Align left" onClick={alignLeft} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'left' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignLeft /></button>
                          <button title="Align center" aria-label="Align center" onClick={alignCenter} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'center' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignCenter /></button>
                          <button title="Align right" aria-label="Align right" onClick={alignRight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'right' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignRight /></button>
                          <button title="Justify" aria-label="Justify" onClick={alignJustify} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'justify' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignJustify /></button>
                        </div>
                        <div className="w-px h-6 bg-white/10" />
                        {/* Links & media */}
                        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Link" aria-label="Link" onClick={addLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiLink /></button>
                          <button title="Unlink" aria-label="Unlink" onClick={removeLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiX /></button>
                          <button
                            title={editorImageUploading ? 'Uploading image...' : 'Image'}
                            aria-label="Image"
                            onClick={addImage}
                            disabled={editorImageUploading}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"
                          >
                            <FiImage />
                          </button>
                        </div>
                        {/* Undo/Redo/Clear */}
                        <div className="ml-auto flex items-center gap-1.5 shrink-0 bg-white/5 rounded-lg px-1 py-1">
                          <button title="Undo" aria-label="Undo" onClick={undo} disabled={!tb.canUndo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCcw /></button>
                          <button title="Redo" aria-label="Redo" onClick={redo} disabled={!tb.canRedo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCw /></button>
                          <button title="Clear formatting" aria-label="Clear formatting" onClick={clearFormatting} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><MdFormatClear /></button>
                        </div>
                      </div>
                      {linkComposerOpen && (
                        <div className="mt-2 rounded-lg border border-white/15 bg-black/35 p-2 flex flex-wrap items-center gap-2">
                          <input
                            value={linkComposerUrl}
                            onChange={(e) => setLinkComposerUrl(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') applyLinkFromComposer() }}
                            placeholder="https://example.com"
                            className="min-w-[220px] flex-1 rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs outline-none focus:border-white/25"
                          />
                          {linkComposerMode === 'text' ? (
                            <input
                              value={linkComposerText}
                              onChange={(e) => setLinkComposerText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') applyLinkFromComposer() }}
                              placeholder="Link text (optional)"
                              className="min-w-[180px] flex-1 rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs outline-none focus:border-white/25"
                            />
                          ) : null}
                          <button type="button" onClick={applyLinkFromComposer} className="rounded border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs hover:bg-white/15">Apply</button>
                          <button type="button" onClick={() => { setLinkComposerOpen(false); setLinkComposerMode('text') }} className="rounded border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10">Cancel</button>
                        </div>
                      )}
                      <div className="pointer-events-none select-none absolute right-2 top-2">
                        <SaveWordStatus dirty={dirty} lastSavedAt={lastSavedAt} wordCount={wordCount} />
                      </div>
                    </div>

                    {/* Rich editor (TipTap) */}
                    <RichEditor
                      ref={richRef}
                      initialHTML={html}
                      onChange={(h, t) => {
                        setHtml(h)
                        setBody(t)
                        setDirty(true)
                      }}
                      onToolbarState={(s) => setTb(s)}
                      onRequestImageLink={(currentUrl) => {
                        setError(null)
                        setLinkComposerMode('image')
                        setLinkComposerText('')
                        setLinkComposerUrl(currentUrl || '')
                        setLinkComposerOpen(true)
                      }}
                    />

                    {autosaveMessage && (
                      <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 flex items-center justify-between gap-3">
                        <span>{autosaveMessage}</span>
                        <button
                          type="button"
                          onClick={() => { if (autosaveRetryRef.current) void autosaveRetryRef.current() }}
                          className="px-2.5 py-1 rounded border border-amber-300/40 bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 text-xs font-semibold"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* Create Pixe Cover (AI) */}
                    <div className="mt-3 rounded-xl border border-white/10 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Create Pixe Cover</div>
                        <button onClick={()=>{
                          setCoverAiOpen((v)=>!v)
                          if (!coverAiPrompt) {
                            setCoverAiPrompt(buildCoverPrompt())
                          }
                        }} className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10">{coverAiOpen ? 'Hide' : 'Open'}</button>
                      </div>
                      {coverAiOpen && (
                        <div className="mt-2 space-y-2">
                          <textarea value={coverAiPrompt} onChange={(e)=>setCoverAiPrompt(e.target.value)} rows={3} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                          <div className="flex items-center gap-2">
                            <button disabled={coverAiLoading} onClick={onGenerateCoverAI} className="px-3 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60">{coverAiLoading ? "Generating..." : "Generate"}</button>
                            {coverAiPreview && (
                              <button onClick={onUseGeneratedCover} className="px-3 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm">Use as cover</button>
                            )}
                            {coverAiError && <div className="text-sm text-red-400">{coverAiError}</div>}
                          </div>
                          {coverAiPreview && (
                            <div className="rounded-lg overflow-hidden border border-white/10">
                              <img src={coverAiPreview} alt="AI cover preview" className="w-full h-auto" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* AI quick tools (selection-aware) */}
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <button onClick={() => runAIMode('Tighten selection', 'Rewrite the selection to be shorter and clearer. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Tighten</button>
                      <button onClick={() => runAIMode('Expand selection', 'Expand the selection with a few more details while keeping the same tone. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Expand</button>
                      <button onClick={() => runAIMode('Bulletize selection', 'Rewrite the selection as a concise bulleted list. Return plain text using lines that start with \"- \".')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Bulletize</button>
                      <button onClick={() => runAIMode('Summarize selection', 'Summarize the selection in 1-3 short paragraphs. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Summarize</button>
                      <button onClick={() => runAIModeAppend('Continue story', 'Continue the narrative in a single short paragraph in first person, building naturally from the latest context. Keep it grounded and specific. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5 bg-white/5">Continue story</button>
                      <button onClick={runAIFormatWhole} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Magazine style (formats entire article)">Magazine style</button>
                      <button onClick={generateAITitle} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Generate title from AI">AI Title</button>
                      <button onClick={generateAISubtitle} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Generate subtitle from AI">AI Subtitle</button>
                    </div>
                    {(titleStatus || subtitleStatus) && (
                      <div className="text-xs text-white/60 mt-1">
                        {titleStatus && <span>{titleStatus}</span>} {subtitleStatus && <span className="ml-2">{subtitleStatus}</span>}
                      </div>
                    )}

                    {/* AI Chat */}
                    <div className="mt-3 rounded-xl border border-white/10 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold">AI Assistant</div>
                          <div className="ml-2 inline-flex rounded-md border border-white/15 overflow-hidden">
                            {(['brainstorm','editor','story'] as const).map((m) => (
                              <button
                                key={m}
                                onClick={() => setAiMode(m)}
                                className={`px-2 py-1 text-xs ${aiMode===m ? 'bg-white/10 text-white' : 'bg-transparent text-white/80 hover:bg-white/5'}`}
                                title={m.charAt(0).toUpperCase()+m.slice(1)}
                              >{m}</button>
                            ))}
                          </div>
                          {aiMode === 'story' && (
                            <label className="ml-2 inline-flex items-center gap-1 text-xs text-white/70">
                              <input type="checkbox" className="accent-brand" checked={autoAppendStory} onChange={(e)=>setAutoAppendStory(e.target.checked)} />
                              Auto-append
                            </label>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowHelp((v)=>!v)}
                          className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10"
                          aria-expanded={showHelp}
                        >{showHelp ? 'Hide help' : 'Help'}</button>
                      </div>
                      {showHelp && (
                        <div className="mb-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/80">
                          <div className="font-semibold text-white/90 mb-1">Modes</div>
                          <ul className="list-disc pl-5 space-y-1">
                            <li><span className="font-semibold">Brainstorm</span>: ideas and outlines in chat only. Insert when you want.</li>
                            <li><span className="font-semibold">Editor</span>: select text first; the AI rewrites only that selection.</li>
                            <li><span className="font-semibold">Story</span>: continues the narrative; can auto-append below the caret.</li>
                          </ul>
                          <div className="font-semibold text-white/90 mt-2 mb-1">Tips</div>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Be explicit: \"Draft the next paragraph\" vs. \"brainstorm titles\".</li>
                            <li>Use selection to keep edits scoped. Undo with Ctrl/Cmd+Z.</li>
                          </ul>
                        </div>
                      )}
                      <div
                        ref={chatListRef}
                        className="max-h-72 overflow-auto space-y-3 pr-1 scroll-smooth"
                        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' } as any}
                      >
                        {messages.length === 0 && (
                          <div className="text-sm text-white/60">Ask for help outlining, rewriting, expanding sections, or proposing a cover image idea. The assistant will use your current draft as context.</div>
                        )}
                        {messages.map((m, idx) => (
                          <div key={idx} className={m.role === 'user' ? 'text-white/90' : 'text-white/80'}>
                            <div className="text-xs uppercase tracking-wide text-white/50 mb-1">{m.role === 'user' ? 'You' : 'Assistant'}</div>
                            <div className="whitespace-pre-wrap text-sm">{m.role === 'assistant' ? editorTextFromHtml(ensureHtmlFromAI(m.content)) : m.content}</div>
                            {m.role === 'assistant' && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                <button onClick={() => applyAssistantReplace(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Apply (selection/body)</button>
                                <button onClick={() => applyAssistantAppend(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Append</button>
                                <button onClick={() => applyAssistantImage(m.content)} disabled={!firstUrlIn(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5 disabled:opacity-50">Use first URL as cover</button>
                              </div>
                            )}
                          </div>
                        ))}
                        <div ref={chatEndRef} />
                        {ai.loading && <div className="text-sm text-white/60">Assistant is thinking...</div>}
                        {(error || ai.error) && <div className="text-sm text-red-400">{error || ai.error}</div>}
                      </div>
                      <div className="mt-3 flex items-end gap-2">
                        <textarea
                          ref={chatRef}
                          rows={1}
                          value={chatInput}
                          onChange={(e)=>setChatInput(e.target.value)}
                          onInput={(e)=>{ const el = e.currentTarget; el.style.height='0px'; el.style.height = el.scrollHeight + 'px' }}
                          className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none resize-none overflow-hidden"
                          wrap="soft"
                          placeholder={authed ? "Ask the assistant..." : "Log in to use AI"}
                          disabled={!authed || ai.loading}
                        />
                        <button onClick={onChatSend} disabled={!authed || !chatInput.trim() || ai.loading} className="px-3 py-2 rounded bg-brand text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60">Send</button>
                      </div>
                    </div>
                  </div>
                </details>

                <PublishActions
                  authed={authed}
                  title={title}
                  saving={saving}
                  onPublish={onPublish}
                  onOpenPublish={() => setPubOpen(true)}
                  onCancel={onCancelPublish}
                  publishDisabled={Boolean(publishDisabledReason)}
                  publishDisabledReason={publishDisabledReason}
                  pixeBalance={walletPixeBalance}
                  publishCostPixe={publishCostPixe}
                  lowBalance={format !== 'job' && !canAffordPublish}
                  accessSummary={accessLabel(accessLevel, accessNeedsPrice ? ppvInt : null, requiredTierRank)}
                />
              </>
            ) : (
              <>
                <TitleFields
                  title={title}
                  subtitle={subtitle}
                  onChangeTitle={setTitle}
                  onChangeSubtitle={setSubtitle}
                  format={format}
                  onChangeFormat={setFormat}
                  category={category}
                  onChangeCategory={setCategory}
                  categories={categories || []}
                />
                {format === 'job' ? jobPanel : localEventPanel}

            {/* Cover uploader */}
            <div className="rounded-xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={createCoverFromContext}
                    disabled={!canGenerateCover || coverAiLoading || uploading}
                    className={`inline-flex items-center rounded-lg border border-white/15 px-3 py-1.5 text-sm ${(!canGenerateCover || coverAiLoading || uploading) ? 'opacity-60' : 'hover:bg-white/5'}`}
                  >
                    {coverAiLoading ? "Creating..." : "Create Pixe Image"}
                  </button>
                  <button
                    type="button"
                    onClick={regenerateCover}
                    disabled={!lastCoverPrompt || coverAiLoading || uploading}
                    className={`inline-flex items-center rounded-lg border border-white/15 px-3 py-1.5 text-sm ${(!lastCoverPrompt || coverAiLoading || uploading) ? 'opacity-60' : 'hover:bg-white/5'}`}
                  >
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCoverAiOpen(true)
                      if (lastCoverPrompt) setCoverAiPrompt(lastCoverPrompt)
                    }}
                    className="text-xs text-white/70 hover:underline"
                  >
                    Adjust prompt
                  </button>
                  <span className="text-xs text-white/60 max-w-xs">
                    {coverStatus
                      ? coverStatus
                      : (!canGenerateCover ? 'Add more text to enable cover generation.' : '')}
                  </span>
                </div>
                <div className="text-xs text-white/60">Upload portrait + landscape below.</div>
              </div>
              <div className="mt-2 text-xs text-white/60">Accepted: JPG, PNG, WebP. Recommended {'>='} 1600px on the long edge.</div>
              {coverModerationMessage ? (
                <div className={`mt-2 text-xs ${coverModerationState === 'blocked' ? 'text-rose-300' : coverModerationState === 'held' ? 'text-amber-300' : 'text-white/70'}`}>
                  {coverModerationMessage}
                </div>
              ) : null}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between h-7 mb-1">
                    <div className="text-xs text-white/60 whitespace-nowrap">Portrait (3:4)</div>
                    <div className="flex items-center gap-2">
                      {coverLandscapeUrl && (
                        <button
                          type="button"
                          className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10"
                          onClick={onGeneratePortrait}
                        >
                          {coverUrl ? 'Regenerate' : 'Generate'}
                        </button>
                      )}
                      {coverUrl && (
                        <button type="button" onClick={onRemovePortrait} className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10">Remove</button>
                      )}
                      <label className="inline-flex items-center rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/5 cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={handlePortraitFileChange} />
                        {uploading ? "Scanning image..." : "Upload"}
                      </label>
                    </div>
                  </div>
                  {renderCoverUrl ? (
                    <img src={renderCoverUrl} alt={publishSettings.coverAlt || 'Cover image'} className="w-full h-40 object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-full h-40 rounded-lg border border-white/10 bg-white/5" />
                  )}
                </div>
                <div className="md:col-span-3">
                  <div className="flex items-center justify-between h-7 mb-1">
                    <div className="text-xs text-white/60 whitespace-nowrap">Landscape (16:9)</div>
                    <div className="flex items-center gap-2">
                      {coverUrl && (
                        <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={onGenerateLandscape}>
                          {coverLandscapeUrl ? 'Regenerate' : 'Generate'}
                        </button>
                      )}
                      {coverLandscapeUrl && (
                        <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={onRemoveLandscape}>Clear</button>
                      )}
                      <label className="inline-flex items-center rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/5 cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={handleLandscapeFileChange} />
                        {uploading ? "Scanning image..." : "Upload"}
                      </label>
                  </div>
                  </div>
                  {renderCoverLandscapeUrl ? (
                    <img src={renderCoverLandscapeUrl} alt={(publishSettings.coverAlt || 'Cover') + ' (landscape)'} className="w-full h-40 object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-full h-40 rounded-lg border border-white/10 bg-white/5" />
                  )}
                </div>
              </div>
              <div className="mt-3">
                <label className="text-sm text-white/70">Alt text</label>
                <input value={publishSettings.coverAlt} onChange={(e)=> setPublishSettings((p)=> ({ ...p, coverAlt: e.target.value }))} className="mt-1 w-full rounded bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" placeholder="Describe the cover for readers and SEO" />
              </div>
            </div>

            {/* Toolbar */}
            <div className="relative text-sm px-2 pt-2 pb-1 rounded-xl bg-neutral-900/60 border border-white/10 shadow-sm pr-64">
              {/* Row 1 */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Inline styles */}
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Bold" aria-label="Bold" onClick={toggleBold} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bold ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiBold /></button>
                  <button title="Italic" aria-label="Italic" onClick={toggleItalic} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.italic ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiItalic /></button>
                  <button title="Strikethrough" aria-label="Strikethrough" onClick={toggleStrike} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.strike ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiStrikethrough2 /></button>
                  <button title="Inline code" aria-label="Inline code" onClick={toggleCode} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.code ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiCode /></button>
                  <button title="Underline" aria-label="Underline" onClick={toggleUnderline} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.underline ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiUnderline /></button>
                </div>
                <div className="w-px h-6 bg-white/10" />
                {/* Headings */}
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Heading 1" aria-label="Heading 1" onClick={setH1} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h1 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H1</span></button>
                  <button title="Heading 2" aria-label="Heading 2" onClick={setH2} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h2 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H2</span></button>
                  <button title="Heading 3" aria-label="Heading 3" onClick={setH3} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h3 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H3</span></button>
                  <button title="Heading 4" aria-label="Heading 4" onClick={setH4} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h4 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H4</span></button>
                </div>
              </div>

              {/* Row 2 */}
              <div className="mt-1 flex flex-nowrap items-center gap-1.5">
                {/* Blocks */}
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Bullet list" aria-label="Bullet list" onClick={toggleBullet} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.bullet ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiList /></button>
                  <button title="Numbered list" aria-label="Numbered list" onClick={toggleOrdered} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.ordered ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiListOrdered /></button>
                  <button title="Quote" aria-label="Quote" onClick={toggleQuote} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.quote ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiDoubleQuotesL /></button>
                  <button title="Highlight" aria-label="Highlight" onClick={toggleHighlight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.highlight ? 'bg-amber-500/30 border-amber-400/40 text-amber-100' : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'}`}><RiMarkPenLine /></button>
                  <button title="Code block" aria-label="Code block" onClick={toggleCodeBlock} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.codeBlock ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><BiCodeBlock /></button>
                  <button title="Divider" aria-label="Divider" onClick={insertDivider} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiMinus /></button>
                </div>
                <div className="w-px h-6 bg-white/10" />
                {/* Alignment */}
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Align left" aria-label="Align left" onClick={alignLeft} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'left' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignLeft /></button>
                  <button title="Align center" aria-label="Align center" onClick={alignCenter} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'center' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignCenter /></button>
                  <button title="Align right" aria-label="Align right" onClick={alignRight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'right' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignRight /></button>
                  <button title="Justify" aria-label="Justify" onClick={alignJustify} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'justify' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignJustify /></button>
                </div>
                <div className="w-px h-6 bg-white/10" />
                {/* Links & media */}
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Link" aria-label="Link" onClick={addLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiLink /></button>
                  <button title="Unlink" aria-label="Unlink" onClick={removeLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiX /></button>
                  <button
                    title={editorImageUploading ? 'Uploading image...' : 'Image'}
                    aria-label="Image"
                    onClick={addImage}
                    disabled={editorImageUploading}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"
                  >
                    <FiImage />
                  </button>
                </div>
                <div className="hidden" />
                {/* Undo/Redo/Clear */}
                <div className="ml-auto flex items-center gap-1.5 shrink-0 bg-white/5 rounded-lg px-1 py-1">
                  <button title="Undo" aria-label="Undo" onClick={undo} disabled={!tb.canUndo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCcw /></button>
                  <button title="Redo" aria-label="Redo" onClick={redo} disabled={!tb.canRedo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCw /></button>
                  <button title="Clear formatting" aria-label="Clear formatting" onClick={clearFormatting} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><MdFormatClear /></button>
                </div>
              </div>
              {linkComposerOpen && (
                <div className="mt-2 rounded-lg border border-white/15 bg-black/35 p-2 flex flex-wrap items-center gap-2">
                  <input
                    value={linkComposerUrl}
                    onChange={(e) => setLinkComposerUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyLinkFromComposer() }}
                    placeholder="https://example.com"
                    className="min-w-[220px] flex-1 rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs outline-none focus:border-white/25"
                  />
                  {linkComposerMode === 'text' ? (
                    <input
                      value={linkComposerText}
                      onChange={(e) => setLinkComposerText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyLinkFromComposer() }}
                      placeholder="Link text (optional)"
                      className="min-w-[180px] flex-1 rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs outline-none focus:border-white/25"
                    />
                  ) : null}
                  <button type="button" onClick={applyLinkFromComposer} className="rounded border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs hover:bg-white/15">Apply</button>
                  <button type="button" onClick={() => { setLinkComposerOpen(false); setLinkComposerMode('text') }} className="rounded border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10">Cancel</button>
                </div>
              )}
              <div className="pointer-events-none select-none absolute right-2 top-2">
                <SaveWordStatus dirty={dirty} lastSavedAt={lastSavedAt} wordCount={wordCount} />
              </div>
            </div>

            {/* Rich editor (TipTap) */}
            <RichEditor
              ref={richRef}
              initialHTML={html}
              onChange={(h, t) => { setHtml(h); setBody(t); setDirty(true) }}
              onToolbarState={(s) => setTb(s)}
              onRequestImageLink={(currentUrl) => {
                setError(null)
                setLinkComposerMode('image')
                setLinkComposerText('')
                setLinkComposerUrl(currentUrl || '')
                setLinkComposerOpen(true)
              }}
            />

            {autosaveMessage && (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 flex items-center justify-between gap-3">
                <span>{autosaveMessage}</span>
                <button
                  type="button"
                  onClick={() => { if (autosaveRetryRef.current) void autosaveRetryRef.current() }}
                  className="px-2.5 py-1 rounded border border-amber-300/40 bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 text-xs font-semibold"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Create Pixe Cover (AI) */}
            <div className="mt-3 rounded-xl border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Create Pixe Cover</div>
                <button onClick={()=>{
                  setCoverAiOpen((v)=>!v)
                  if (!coverAiPrompt) {
                    setCoverAiPrompt(buildCoverPrompt())
                  }
                }} className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10">{coverAiOpen ? 'Hide' : 'Open'}</button>
              </div>
              {coverAiOpen && (
                <div className="mt-2 space-y-2">
                  <textarea value={coverAiPrompt} onChange={(e)=>setCoverAiPrompt(e.target.value)} rows={3} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                  <div className="flex items-center gap-2">
                    <button disabled={coverAiLoading} onClick={onGenerateCoverAI} className="px-3 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60">{coverAiLoading ? "Generating..." : "Generate"}</button>
                    {coverAiPreview && (
                      <button onClick={onUseGeneratedCover} className="px-3 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm">Use as cover</button>
                    )}
                    {coverAiError && <div className="text-sm text-red-400">{coverAiError}</div>}
                  </div>
                  {coverAiPreview && (
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      <img src={coverAiPreview} alt="AI cover preview" className="w-full h-auto" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI quick tools (selection-aware) */}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <button onClick={() => runAIMode('Tighten selection', 'Rewrite the selection to be shorter and clearer. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Tighten</button>
              <button onClick={() => runAIMode('Expand selection', 'Expand the selection with a few more details while keeping the same tone. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Expand</button>
              <button onClick={() => runAIMode('Bulletize selection', 'Rewrite the selection as a concise bulleted list. Return plain text using lines that start with "- ".')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Bulletize</button>
              <button onClick={() => runAIMode('Summarize selection', 'Summarize the selection in 1-3 short paragraphs. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Summarize</button>
              <button onClick={() => runAIModeAppend('Continue story', 'Continue the narrative in a single short paragraph in first person, building naturally from the latest context. Keep it grounded and specific. Return plain text (no HTML).')} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5 bg-white/5">Continue story</button>
              <button onClick={runAIFormatWhole} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Magazine style (formats entire article)">Magazine style</button>
              <button onClick={generateAITitle} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Generate title from AI">AI Title</button>
              <button onClick={generateAISubtitle} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5" title="Generate subtitle from AI">AI Subtitle</button>
            </div>
            {(titleStatus || subtitleStatus) && (
              <div className="text-xs text-white/60 mt-1">
                {titleStatus && <span>{titleStatus}</span>} {subtitleStatus && <span className="ml-2">{subtitleStatus}</span>}
              </div>
            )}

                {/* Draft controls */}
                <DraftControls
                  onSaveDraft={onSaveDraft}
                  currentDraftId={currentDraftId}
                  draftsList={draftsList}
                  onSelectDraft={onSelectDraft}
                  onDeleteDraft={onDeleteDraft}
                  statusText={bodySaveStatusText}
                  statusTone={bodySaveStatusTone}
                />

            {/* AI Chat */}
            <div className="mt-3 rounded-xl border border-white/10 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold">AI Assistant</div>
                  <div className="ml-2 inline-flex rounded-md border border-white/15 overflow-hidden">
                    {(['brainstorm','editor','story'] as const).map((m) => (
                      <button
                        key={m}
                        onClick={() => setAiMode(m)}
                        className={`px-2 py-1 text-xs ${aiMode===m ? 'bg-white/10 text-white' : 'bg-transparent text-white/80 hover:bg-white/5'}`}
                        title={m.charAt(0).toUpperCase()+m.slice(1)}
                      >{m}</button>
                    ))}
                  </div>
                  {aiMode === 'story' && (
                    <label className="ml-2 inline-flex items-center gap-1 text-xs text-white/70">
                      <input type="checkbox" className="accent-brand" checked={autoAppendStory} onChange={(e)=>setAutoAppendStory(e.target.checked)} />
                      Auto-append
                    </label>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowHelp((v)=>!v)}
                  className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10"
                  aria-expanded={showHelp}
                >{showHelp ? 'Hide help' : 'Help'}</button>
              </div>
              {showHelp && (
                <div className="mb-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/80">
                  <div className="font-semibold text-white/90 mb-1">Modes</div>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><span className="font-semibold">Brainstorm</span>: ideas and outlines in chat only. Insert when you want.</li>
                    <li><span className="font-semibold">Editor</span>: select text first; the AI rewrites only that selection.</li>
                    <li><span className="font-semibold">Story</span>: continues the narrative; can auto-append below the caret.</li>
                  </ul>
                  <div className="font-semibold text-white/90 mt-2 mb-1">Tips</div>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Be explicit: "Draft the next paragraph" vs. "brainstorm titles".</li>
                    <li>Use selection to keep edits scoped. Undo with Ctrl/Cmd+Z.</li>
                  </ul>
                </div>
              )}
              <div
                ref={chatListRef}
                className="max-h-72 overflow-auto space-y-3 pr-1 scroll-smooth"
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' } as any}
              >
                {messages.length === 0 && (
                  <div className="text-sm text-white/60">Ask for help outlining, rewriting, expanding sections, or proposing a cover image idea. The assistant will use your current draft as context.</div>
                )}
                {messages.map((m, idx) => (
                  <div key={idx} className={m.role === 'user' ? 'text-white/90' : 'text-white/80'}>
                    <div className="text-xs uppercase tracking-wide text-white/50 mb-1">{m.role === 'user' ? 'You' : 'Assistant'}</div>
                    <div className="whitespace-pre-wrap text-sm">{m.role === 'assistant' ? editorTextFromHtml(ensureHtmlFromAI(m.content)) : m.content}</div>
                    {m.role === 'assistant' && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <button onClick={() => applyAssistantReplace(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Apply (selection/body)</button>
                        <button onClick={() => applyAssistantAppend(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Append</button>
                        <button onClick={() => applyAssistantImage(m.content)} disabled={!firstUrlIn(m.content)} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5 disabled:opacity-50">Use first URL as cover</button>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
                {ai.loading && <div className="text-sm text-white/60">Assistant is thinking...</div>}
                {(error || ai.error) && <div className="text-sm text-red-400">{error || ai.error}</div>}
              </div>
              <div className="mt-3 flex items-end gap-2">
                <textarea
                  ref={chatRef}
                  rows={1}
                  value={chatInput}
                  onChange={(e)=>setChatInput(e.target.value)}
                  onInput={(e)=>{ const el = e.currentTarget; el.style.height='0px'; el.style.height = el.scrollHeight + 'px' }}
                  className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none resize-none overflow-hidden"
                  wrap="soft"
                  placeholder={authed ? "Ask the assistant..." : "Log in to use AI"}
                  disabled={!authed || ai.loading}
                />
                <button onClick={onChatSend} disabled={!authed || !chatInput.trim() || ai.loading} className="px-3 py-2 rounded bg-brand text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60">Send</button>
              </div>
            </div>

            {(error && !ai.error) && <div className="text-sm text-red-400">{error}</div>}
            {/* Access */}
            <div className="mt-3 rounded-xl border border-white/10 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Access</div>
                  <div className="mt-1 text-xs text-white/60">Choose who can read this Pixe.</div>
                </div>
                <div className="text-xs text-white/70">
                  <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1">
                    {accessLabel(accessLevel, accessNeedsPrice ? ppvInt : null, requiredTierRank)}
                  </span>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {(
                  [
                    { key: 'FREE', label: 'Free' },
                    { key: 'SUBSCRIBERS_ONLY', label: 'Subscribers' },
                    { key: 'PAY_PER_VIEW', label: 'PPV' },
                    { key: 'SUBSCRIBERS_OR_PPV', label: 'Subs or PPV' },
                  ] as Array<{ key: AccessLevel; label: string }>
                ).map((opt) => {
                  const disabled = isAccessOptionDisabled({ option: opt.key, subsEnabled, ppvAllowed })
                  const active = accessLevel === opt.key
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => onPickAccess(opt.key)}
                      className={[
                        'px-3 py-2 rounded-lg border text-sm font-semibold transition',
                        active ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10',
                        disabled ? 'opacity-50 cursor-not-allowed hover:bg-white/5' : '',
                      ].join(' ')}
                      title={
                        disabled && !subsEnabled
                          ? 'Enable subscriptions to use subscriber-only or PPV access.'
                          : disabled && subsEnabled && !ppvAllowed
                            ? 'Turn on pay-per-view in Subscriptions settings.'
                            : undefined
                      }
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>

              {!subsEnabled ? (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                  Enable subscriptions to use Subscriber-only or PPV access.{' '}
                  <Link className="underline" to="/settings/creator/subscriptions">
                    Open settings
                  </Link>
                </div>
              ) : subsEnabled && !ppvAllowed ? (
                <div className="mt-3 text-xs text-white/60">
                  Pay-per-view is off. Turn it on in{' '}
                  <Link className="underline" to="/settings/creator/subscriptions">
                    Subscriptions settings
                  </Link>
                  .
                </div>
              ) : null}

              {subsEnabled && hasTiers && (accessLevel === 'SUBSCRIBERS_ONLY' || accessLevel === 'SUBSCRIBERS_OR_PPV') ? (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-white/70">Minimum tier</div>
                  <select
                    value={requiredTierRank ? String(requiredTierRank) : ''}
                    onChange={(e) => {
                      const raw = (e.target.value || '').trim()
                      if (!raw) setRequiredTierRank(null)
                      else setRequiredTierRank(Number(raw))
                    }}
                    className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                    style={{ colorScheme: 'dark' } as any}
                  >
                    <option value="">Any subscriber</option>
                    {(creatorTiers || [])
                      .filter((t) => t.is_active)
                      .sort((a, b) => (a.rank || 0) - (b.rank || 0))
                      .map((t) => (
                        <option key={t.id} value={String(t.rank)}>
                          {t.name} (Tier {t.rank}+)
                        </option>
                      ))}
                  </select>
                  <div className="mt-1 text-xs text-white/60">Higher tiers include lower-tier access.</div>
                </div>
              ) : null}

              {accessNeedsPrice ? (
                <div className="mt-3 grid gap-2 md:grid-cols-2 items-start">
                  <div>
                    <div className="text-xs font-semibold text-white/70">Unlock price (PIXE)</div>
                    <input
                      value={ppvPrice}
                      onChange={(e) => setPpvPrice(e.target.value)}
                      placeholder={String(defaultPpv > 0 ? defaultPpv : 1)}
                      className={[
                        'mt-1 w-full rounded-lg bg-white/5 border px-3 py-2 text-sm outline-none',
                        accessPriceInvalid ? 'border-red-500/30 focus:border-red-500/40' : 'border-white/10 focus:border-white/20',
                      ].join(' ')}
                      inputMode="numeric"
                    />
                    <div className="mt-1 text-xs text-white/60">Non-subscribers can unlock a single Pixe.</div>
                    {accessPriceInvalid ? <div className="mt-1 text-xs text-red-300">Enter a whole number (1+).</div> : null}
                  </div>
                  <div className="text-xs text-white/60 leading-relaxed">
                    Subscribers auto-unlock. If you choose PPV, the global feed shows a preview with an unlock CTA.
                  </div>
                </div>
              ) : null}
            </div>
                <PublishActions
                  authed={authed}
                  title={title}
                  saving={saving}
                  onPublish={onPublish}
                  onOpenPublish={() => setPubOpen(true)}
                  onCancel={onCancelPublish}
                  publishDisabled={Boolean(publishDisabledReason)}
                  publishDisabledReason={publishDisabledReason}
                  pixeBalance={walletPixeBalance}
                  publishCostPixe={publishCostPixe}
                  lowBalance={format !== 'job' && !canAffordPublish}
                  accessSummary={accessLabel(accessLevel, accessNeedsPrice ? ppvInt : null, requiredTierRank)}
                />
              </>
            )}
          </section>

          {/* Preview */}
          <aside className="lg:col-span-5">
            {format === 'micro' ? (
              <div className="hidden lg:block">
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 text-sm text-white/60">Micro (Cover)</div>
                    <MicroPreviewCard
                      text={microText}
                      templateId={microTemplateId}
                      categoryLabel={microCategoryName || null}
                      authorName={me?.profile?.display_name || me?.profile?.handle || me?.username || null}
                      authorHandle={me?.profile?.handle || me?.username || null}
                      authorAvatarUrl={me?.profile?.avatar_url || null}
                      createdAt={new Date().toISOString()}
                      viewsCount={0}
                      hasProof={false}
                    />
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <div className="mb-2 text-sm text-white/60">Reader page</div>
                    <MicroReaderPreview
                      variant="compact"
                      microText={microText}
                      categoryLabel={microCategoryName || null}
                      authorName={me?.profile?.display_name || me?.profile?.handle || me?.username || null}
                      authorHandle={me?.profile?.handle || me?.username || null}
                      authorAvatarUrl={me?.profile?.avatar_url || null}
                      createdAt={new Date().toISOString()}
                      viewsCount={0}
                      hasProof={false}
                      html={html}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm text-white/60">Preview</div>
                  <div className="flex items-center gap-1.5">
                    {(['auto','portrait','landscape'] as const).map((k) => {
                      const disabled =
                        (k === 'portrait' && !(coverUrl || coverLandscapeUrl)) ||
                        (k === 'landscape' && !(coverLandscapeUrl || coverUrl))
                      const active = previewTab === k
                      return (
                        <button
                          key={k}
                          onClick={() => setPreviewTab(k)}
                          disabled={disabled}
                          className={`text-xs px-2.5 py-1 rounded border ${active ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/10 hover:bg-white/10'} disabled:opacity-50`}
                        >
                          {k[0].toUpperCase() + k.slice(1)}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <PreviewPanel
                  title={title}
                  subtitle={subtitle}
                  format={format}
                  previewImage={previewImage}
                  portraitImage={renderCoverUrl || null}
                  landscapeImage={renderCoverLandscapeUrl || null}
                  publishAlt={publishSettings.coverAlt}
                  html={html}
                />
              </>
            )}
          </aside>
        </div>
      </main>
      <input
        ref={editorImageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/*"
        className="hidden"
        onChange={handleEditorImageFileChange}
      />
      {/* Publish Settings Drawer */}
      <PublishSettingsDrawer
        open={pubOpen}
        onClose={() => setPubOpen(false)}
        settings={publishSettings}
        onChange={(next) => setPublishSettings((p) => ({ ...p, ...next }))}
        urlBase={`${window.location.origin}/p/`}
        computedTitleSlug={slugifyPreview(title)}
      />
      <InsufficientPixeModal
        open={Boolean(insufficientPixeGate)}
        requiredPixe={insufficientPixeGate?.required_pixe || publishCostPixe}
        currentPixe={insufficientPixeGate?.current_pixe || walletPixeBalance || '0'}
        shortfallPixe={insufficientPixeGate?.shortfall_pixe || '0'}
        onClose={() => setInsufficientPixeGate(null)}
        onBuy={() => {
          const next = insufficientPixeGate?.top_up_url || '/settings/wallet'
          setInsufficientPixeGate(null)
          nav(next)
        }}
      />
    </div>
  )
}

function editorTextFromHtml(h: string): string {
  try {
    const el = document.createElement('div')
    el.innerHTML = h
    return el.innerText.replace(/\u00A0/g, ' ').trim()
  } catch {
    return h
  }
}

function normalizeWhitespace(text: string): string {
  return String(text || '').replace(/\s+/g, ' ').trim()
}

function tokenizeWords(text: string): string[] {
  return (text || '')
    .toLowerCase()
    .match(/[a-z0-9']+/g)
    ?.map((t) => t)
    ?? []
}

function countWords(text: string): number {
  const t = String(text || '').replace(/\u00A0/g, ' ').trim()
  if (!t) return 0
  const tokens = t.match(/\S+/g)
  return tokens ? tokens.length : 0
}

function formatTime(ts: number): string {
  try {
    const d = new Date(ts)
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
function textToHtmlParagraphs(text: string): string {
  const parts = (text || '').split(/\r?\n+/).filter(Boolean)
  if (parts.length === 0) return '<p></p>'
  return parts.map((p) => `<p>${escapeHtml(p)}</p>`).join('')
}

function kmToDistancePreset(km: unknown): LocalDistanceOption {
  const n = Number(km || 0)
  if (!Number.isFinite(n) || n <= 0) return 'city'
  const miles = Math.round(n / 1.60934)
  if (miles <= 7) return 5
  if (miles <= 17) return 10
  if (miles <= 37) return 25
  if (miles <= 75) return 50
  return 100
}

function ensureHtmlFromAI(s: string): string {
  const str = String(s || '').trim()
  if (!str) return '<p></p>'
  // If looks like HTML, pass through; StarterKit will drop unknown nodes.
  if (/<\w+[^>]*>/.test(str)) return str
  return textToHtmlParagraphs(str)
}

async function transcodeToJpeg(file: File): Promise<File | null> {
  try {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = reject
      i.src = dataUrl
    })
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(img, 0, 0)
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (!blob) return null
    const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg'
    // @ts-ignore
    return new File([blob], name, { type: 'image/jpeg' })
  } catch {
    return null
  }
}

async function dataUrlToFile(dataUrl: string, filename: string): Promise<File | null> {
  try {
    if (!dataUrl.startsWith('data:')) return null
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    // @ts-ignore
    return new File([blob], filename, { type: blob.type || 'image/png' })
  } catch {
    return null
  }
}

async function urlToFile(url: string, filename: string): Promise<File | null> {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    // @ts-ignore
    return new File([blob], filename, { type: blob.type || 'image/png' })
  } catch {
    return null
  }
}

function slugifyPreview(input: string): string {
  const s = (input || '').toLowerCase()
  return s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}







